// Rio de Janeiro, "Copacabana summer night".
// Back to front: dithered purple-to-teal sky, stars and a big moon; the Tijuca ridge and Corcovado crowned by a
// floodlit Christ the Redeemer; Morro da Urca and Sugarloaf with the cable car; favela hills full of warm lights;
// the far curve of beachfront hotels with a floodlit rooftop billboard; the bay (reflections, glittering moon path,
// a boat, fireworks); wave sets rolling onto Copacabana, a couple strolling the waterline, a futevôlei game at the net;
// kiosks, palms and the wavy calçadão with its strollers; the avenue; and our balcony: fairy lights on the railing,
// a woman swaying in the hammock with her caipirinha, her radio and another caipirinha on the cap, monstera and
// bird-of-paradise, and fireflies at three depths.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, cycle, hash, rng, bayer, hex,
  makeCanvas, px, rect, ditherFill, makeHalo, drawHalo, reflect, streak, text, textWidth, FONT3, makePitch, gradeAd } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                              // this scene's 480x270 buffer, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);
const HXC = {};
const HX = h => HXC[h] || (HXC[h] = hex(h));          // cached #rrggbb -> [r, g, b]

/* ================= Scene constants ================= */
const HZ = 148;                                       // sea horizon = far shoreline
const MOON = { x: 392, y: 44, r: 11 };
const XC = 158;                                       // Christ the Redeemer (focusX), statue centre is x = 157.5
const CORC = 50;                                      // Corcovado summit platform (statue stands on this row)
const SUG = { cx: 325, top: 72 };                     // Pão de Açúcar
const URC = { cx: 262, top: 111 };                    // Morro da Urca
const LEME = { cx: 214, top: 124 };                   // Morro do Leme, the far end of the beach
const CAB = { x0: 271, y0: 108, x1: 326, y1: 70, sag: 5 };      // cable Urca -> Sugarloaf
const shoreF = x => 193 - 8 * ((W - x) / W) ** 2;     // near shoreline; the crescent bends away to the left
const shoreY = x => Math.round(shoreF(x));
const SAND_H = 17;
const promY = x => shoreY(x) + SAND_H;                // top edge of the calçadão
const PROM_H = 7;
const LEDGE = 228;                                    // balcony ledge (top face)
const PIL = 464;                                      // right pillar (left face)
const POST = 300;                                     // hammock post
const HOOK_L = { x: POST + 1, y: 213 }, HOOK_R = { x: PIL - 1, y: 211 };

/* ================= Sky ================= */
const SKY_T = ['#130930', '#180b38', '#1e0e41', '#25104a', '#2c1452', '#33185a', '#3a1d62', '#40236a',
  '#432b72', '#403678', '#3b427e', '#354f84', '#305d88', '#2c6b8a', '#2c7889', '#318487', '#3a8f86'].map(hex);
const SKY_R = ['#130930', '#180b38', '#1e0e41', '#25104a', '#2c1452', '#36185a', '#40205e', '#4a2664',
  '#552d68', '#60346c', '#6a3b6e', '#744270', '#7e4a70', '#88526e', '#905a6c', '#98626a', '#9e6a68'].map(hex);
function renderSky() {
  const h = HZ + 2, [c, x] = makeCanvas(W, h);
  const img = x.createImageData(W, h), d = img.data;
  for (let y = 0; y < h; y++) for (let i = 0; i < W; i++) {
    let v = Math.pow(Math.min(1, y / HZ), 1.45);
    v += Math.max(0, 1 - Math.hypot((i - MOON.x) / 150, (y - MOON.y) / 105)) * 0.3;     // moonlit haze
    const f = Math.min(1, v) * (SKY_T.length - 1);
    let k = Math.floor(f);
    if (f - k > bayer(i, y)) k++;
    k = Math.min(k, SKY_T.length - 1);
    const warm = smooth(250, 40, i) * smooth(20, 112, y) * 0.95;                       // city glow behind the hills
    const col = warm > bayer(i + 2, y + 1) ? SKY_R[k] : SKY_T[k], o = (y * W + i) * 4;
    d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2]; d[o + 3] = 255;
  }
  x.putImageData(img, 0, 0);
  return c;
}
const STARS = [];
{
  const r = rng(21);
  while (STARS.length < 130) {
    const x = Math.floor(r() * W), y = Math.floor(Math.pow(r(), 1.5) * 122);
    if (Math.hypot(x - MOON.x, y - MOON.y) < 25) continue;
    const c = r();
    STARS.push({ x, y, k: 13 + Math.floor(r() * 80), ph: r(), b: 0.25 + r() * 0.6, big: r() < 0.07,
      col: c < 0.7 ? '#e6ecff' : c < 0.85 ? '#fff0d2' : '#c8fff2' });
  }
}
function renderMoon() {
  const r = MOON.r, s = r * 2 + 1; const [c, x] = makeCanvas(s, s);
  const craters = [[-4, -3, 2.2], [3, 2, 1.8], [-1, 5, 1.5], [4, -5, 1.2], [-6, 3, 1.1], [1, -1, 1.0], [6, 1, 0.9]];
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - r, dy = y - r, dd = Math.hypot(dx, dy);
    if (dd > r + 0.35) continue;
    let col = dx + dy < -8 ? '#fffbe8' : dx + dy < 4 ? '#f7f0d6' : '#ece2c4';
    for (const [cx, cy, cr] of craters) if (Math.hypot(dx - cx, dy - cy) < cr) col = dx + dy < 0 ? '#e4dabb' : '#d9cead';
    if (dd > r - 1.1) col = dx + dy > 2 ? '#c9c7ae' : '#efe8cf';
    px(x, i, y, col);
  }
  return c;
}

/* ================= Mountain silhouettes ================= */
const tijucaY = x => 94 + 9 * Math.sin(x * 0.024 + 1.1) + 5 * Math.sin(x * 0.061 + 0.3) + 2.5 * Math.sin(x * 0.17 + 2)
  + Math.max(0, x - 236) * 0.4;
function corcY(x) {                                   // Corcovado: statue platform, steep flanks, near-sheer right face
  const dx = x - XC + 0.5, a = Math.abs(dx);
  if (a <= 6) return CORC;
  if (dx < 0) return CORC + Math.pow(a - 6, 1.2) * 1.45 - 5 * Math.exp(-(((a - 24) / 6) ** 2)) + (a > 16 ? Math.sin(x * 0.8) : 0);   // the hunch
  return CORC + Math.pow(a - 6, 1.12) * 3.0;
}
const bump = (x, c, top, w, k) => top + k * ((x - c) / w) ** 2;
const hillY = x => Math.min(bump(x, 40, 88, 50, 34), bump(x, 124, 97, 44, 30), bump(x, 190, 111, 36, 28));
const lemeY = x => bump(x, LEME.cx, LEME.top, 34, 24);
function urcaY(x) {                                  // Morro da Urca: a lumpy forested hump, steeper toward Sugarloaf
  const dx = x - URC.cx, a = Math.max(0, Math.abs(dx) - 4);
  return URC.top + a * a * (dx < 0 ? 0.03 : 0.052) + 1.2 * Math.sin(x * 0.45) * Math.min(1, a / 6) + (dx > 12 ? (dx - 12) * 0.6 : 0);
}
function sugarRow(y) {                                // Sugarloaf: [x0, x1] covered on row y (or null)
  const dy = y - SUG.top, d = dy / (HZ - SUG.top);
  if (d < 0) return null;
  const crown = 14 * Math.sqrt(1 - (1 - Math.min(1, (dy + 0.5) / 12)) ** 2);    // the blunt, rounded summit
  const hw = Math.max(crown, 1.5 + 27 * Math.pow(1 - Math.pow(1 - Math.min(1, d), 2), 0.62)) + Math.max(0, d - 0.85) * 22;
  const cx = SUG.cx + 4 * Math.pow(1 - d, 1.5);
  return [Math.round(cx - hw), Math.round(cx + hw)];
}
// Christ the Redeemer: head, outstretched arms with sleeves hanging to the chest, the robe falling straight to the
// feet, pedestal (22 x 24)
const CHRIST = [
  '..........##..........', '..........##..........', '.........####.........', '.####################.',
  '######################', '....##############....', '......##########......', '.......########.......',
  '........######........', '........######........', '........######........', '........######........',
  '........######........', '........######........', '........######........', '........######........',
  '........######........', '........######........', '........######........', '......##########......',
  '.......########.......', '.......########.......', '.......########.......', '......##########......',
];

/* ================= Far shore: the curve of beachfront hotels ================= */
const HOTELS = [], HWIN = [];
{
  const r = rng(33);
  let bx = 0;
  while (bx < 184) {
    const far = bx / 184, first = bx === 0;
    const w = first ? 24 : Math.round(lerp(12, 6, far) + r() * 5);
    const h = first ? 30 : Math.max(5, Math.round(lerp(25, 7, Math.pow(far, 0.8)) + (r() - 0.5) * 9));
    const b = { x: bx, w, h, top: HZ - 4 - h, tone: r() };
    HOTELS.push(b);
    for (let wy = b.top + 2; wy < HZ - 5; wy += 2) for (let wx = bx + 1; wx < bx + w - 1; wx += 2) {
      const v = r();
      if (v < 0.1) continue;
      HWIN.push({ id: HWIN.length, x: wx, y: wy, tog: v < 0.4, lit: v > 0.66, n: 2 + Math.floor(r() * 7), p: 0.3 + r() * 0.4,
        off: r(), col: r() < 0.82 ? (r() < 0.6 ? '#ffd48a' : '#ffb865') : '#c4e4ff' });
    }
    bx += w + (r() < 0.3 ? 1 : 0);
  }
}
const HTOG = HWIN.filter(w => w.tog);
const hotelTop = new Int16Array(W).fill(HZ);
for (const b of HOTELS) for (let i = b.x; i < b.x + b.w && i < W; i++) hotelTop[i] = b.top;
const BEACONS = HOTELS.filter(b => b.h >= 20).map(b => ({ x: b.x + (b.w >> 1), y: b.top - 1 }));
const SIGN = { x: HOTELS[0].x + 2, y: HOTELS[0].top - 8 };
// The billboard: a painted panel, floodlit from the rail at its foot, on short legs on the roof of an old three-storey
// beachfront hotel. The hotel stands in front of the far row, nearer along the curve of the beach, and is drawn at
// its own scale: a storey is 6 px (three of the far row's), so the face is three storeys tall and the whole sign,
// frame to roof, under four. BB is the face (exported as `billboard`): the live site paints its faces there,
// everything else shows a made-up acai bar ad. The hotel exists only with the sign.
const BB = { x: 71, y: 107, w: 42, h: 18 };
const BB_DECK = BB.y + BB.h + 1;                      // the lamp rail, the row under the frame
const BB_LAMPS = [BB.x + 4, BB.x + 20, BB.x + 36];    // floodlamps on the rail (2 px wide), aimed up at the face
const BB_HOST = { x0: 67, x1: 116, top: BB_DECK + 3, floor: 6 };   // the hotel: it spans four of the far row's, roof to shore
const BB_LEGS = [BB.x + 1, BB.x + 14, BB.x + 27, BB.x + 39];       // legs (2 px wide) from the rail down to the roof
const BB_FLICK = [[33.9, 35.3], [121.2, 122.0], [187.5, 188.9]];   // the middle lamp acting up now and then

/* ================= Favela lights (tiny warm lights clustered on the hill slopes) ================= */
const FAV = [];
const FAV_COLS = ['#ffd88a', '#ffc464', '#fff0c8', '#ffab52', '#ffd88a', '#ffe2a6', '#bfe4ff'];
const favZone = (i, y) => Math.sin(i * 0.11 + 1.7) * Math.sin(y * 0.23 + i * 0.03) + 0.8 * Math.sin((i - y * 0.6) * 0.07 + 0.5)
  + (hash(i >> 2, y >> 2, 5) - 0.5) * 0.7;
const cableY = s => lerp(CAB.y0, CAB.y1, s) + CAB.sag * 4 * s * (1 - s);

/* ================= Mountains, favelas and the far shore (one static canvas) ================= */
function renderMountains() {
  const [c, x] = makeCanvas(W, HZ);
  const img = x.createImageData(W, HZ), d = img.data;
  const put = (i, y, h) => {
    if (i < 0 || i >= W || y < 0 || y >= HZ) return;
    const o = (y * W + i) * 4, col = HX(h);
    d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2]; d[o + 3] = 255;
  };
  // far Tijuca ridge, hazy blue
  for (let i = 0; i < 300; i++) {
    const top = Math.round(tijucaY(i));
    for (let y = Math.max(0, top); y < HZ; y++) {
      const dd = y - top;
      let col = dd === 0 ? '#36486c' : dd < 3 ? (bayer(i, y) < 0.5 ? '#2a3c5e' : '#233352') : '#1d2b47';
      if (dd > 2 && hash(i >> 1, y >> 1, 3) < 0.2) col = '#1a2742';
      put(i, y, col);
    }
  }
  // Corcovado: dark forest, moonlit right face, floodlit summit rock
  for (let i = XC - 50; i <= XC + 32; i++) {
    const top = Math.round(corcY(i));
    if (top >= HZ) continue;
    const topR = Math.round(corcY(i + 1)), topL = Math.round(corcY(i - 1));
    for (let y = top; y < HZ; y++) {
      const dd = y - top, b = bayer(i, y);
      let col = hash(i, y >> 1, 9) < 0.3 ? '#15283c' : '#112134';
      if (i > XC && y < topR) col = dd < 1 ? '#46648a' : '#2e4a68';
      else if (i < XC && y < topL + 1 && dd < 2) col = '#2e2a4e';
      const lit = 1 - Math.hypot((i - XC + 0.5) / 12, (y - CORC) / 9);
      if (lit > 0.12 + b * 0.55) col = lit > 0.62 ? '#566a92' : lit > 0.38 ? '#3e507a' : '#2c3c60';
      put(i, y, col);
    }
  }
  // favela hills: forest tops, slopes of stacked little houses; collect the lights
  FAV.length = 0;
  const r = rng(77);
  for (let i = 0; i < 240; i++) {
    const top = Math.round(hillY(i));
    if (top >= HZ) continue;
    const lm = Math.round(lemeY(i));
    for (let y = top; y < HZ; y++) {
      const dd = y - top, b = bayer(i, y);
      let col = hash(i, y, 11) < 0.25 ? '#132624' : '#0f1f1f';
      if (dd === 0) col = '#24403c'; else if (dd === 1 && b < 0.5) col = '#1a302e';
      const z = favZone(i, y);
      if (dd > 3 && z > 0.05) {
        const hv = r();
        col = hv < 0.33 ? '#2c2327' : hv < 0.58 ? '#221c22' : hv < 0.68 ? '#3b2d2b' : col;
        const inSign = i >= SIGN.x - 2 && i <= SIGN.x + 21 && y >= SIGN.y - 2 && y <= SIGN.y + 8;
        if (!inSign && y < hotelTop[i] - 1 && y < lm - 1 && r() < 0.15 + z * 0.1) {
          const tw = r() < 0.4, dim = !tw && r() < 0.3;
          FAV.push({ id: FAV.length, x: i, y, w: r() < 0.12 ? 2 : 1, tw, k: 20 + Math.floor(r() * 90), ph: r(),
            n: 3 + Math.floor(r() * 8), off: r(), col: dim ? '#a8784a' : FAV_COLS[Math.floor(r() * FAV_COLS.length)] });
        }
      }
      put(i, y, col);
    }
  }
  // Morro da Urca
  for (let i = URC.cx - 32; i <= URC.cx + 32; i++) {
    const top = Math.round(urcaY(i));
    if (top >= HZ) continue;
    const topR = Math.round(urcaY(i + 1));
    for (let y = top; y < HZ; y++) {
      let col = y > HZ - 16 ? (hash(i, y, 13) < 0.4 ? '#13241f' : '#0f1d1c') : (hash(i >> 1, y, 14) < 0.2 ? '#1a2638' : '#16222f');
      if (i > URC.cx && y < topR) col = '#34506c';
      if (y === top) col = i > URC.cx - 4 ? '#3e5876' : '#2a3a52';
      put(i, y, col);
    }
  }
  // Sugarloaf: granite dome with vertical seams, moonlit right flank, green skirt at the waterline
  for (let y = SUG.top; y < HZ; y++) {
    const [x0, x1] = sugarRow(y), up = sugarRow(y - 1), dd = (y - SUG.top) / (HZ - SUG.top);
    for (let i = x0; i <= x1; i++) {
      const fromR = x1 - i, fromL = i - x0;
      let col = hash(i, y >> 2, 19) < 0.16 ? '#18233a' : hash(i >> 1, y >> 3, 20) < 0.2 ? '#1f2e44' : '#1c2a3e';
      if (fromR < 2 + dd * 3) col = fromR === 0 ? '#5a7a9a' : '#3e5a7a';
      else if (fromR < 5 + dd * 6 && bayer(i, y) < 0.5) col = '#2c4058';
      if (fromL === 0) col = '#2e2e4e';
      if (!up || i < up[0] || i > up[1]) col = i > SUG.cx ? '#6484a4' : '#40587a';
      if (dd > 0.8 && (dd - 0.8) * 5 > bayer(i, y) * 0.8 + (hash(i >> 1, 0, 15) - 0.5) * 0.5) col = bayer(i, y) < 0.3 ? '#16281f' : '#0f1d1c';
      put(i, y, col);
    }
  }
  // Morro do Leme at the far end of the beach
  for (let i = LEME.cx - 36; i <= LEME.cx + 36; i++) {
    const top = Math.round(lemeY(i));
    if (top >= HZ) continue;
    for (let y = top; y < HZ; y++) {
      let col = hash(i, y, 17) < 0.3 ? '#12221f' : '#0d1a19';
      if (y === top) col = i > LEME.cx ? '#2a4644' : '#1e3432';
      put(i, y, col);
    }
  }
  // Niterói hills, a low strip on the horizon right of Sugarloaf
  for (let i = 358; i < PIL; i++) {
    const top = HZ - 1 - Math.round(2.2 * Math.sin(clamp((i - 358) / 96, 0, 1) * Math.PI) + (i > 380 && i < 400 ? 1 : 0));
    for (let y = top; y < HZ; y++) put(i, y, y === top ? '#2a3e52' : '#1c2c3e');
  }
  x.putImageData(img, 0, 0);

  for (const f of FAV) {                             // light spilling onto the walls next to each lamp
    if (hash(f.id, 1, 44) < 0.5) px(x, f.x - 1, f.y, '#4a3830');
    if (hash(f.id, 2, 44) < 0.5) px(x, f.x + f.w, f.y, '#43322c');
    if (hash(f.id, 3, 44) < 0.3) px(x, f.x, f.y + 1, '#3a2c28');
  }
  for (const f of FAV) if (!f.tw) rect(x, f.x, f.y, f.w, 1, f.col);
  px(x, 241, 142, '#ffd890'); rect(x, 236, 144, 2, 1, '#ffc070'); px(x, 229, 139, '#ffe0a0'); px(x, 246, 146, '#ffcf80');
  for (const [lx, ly, col] of [[284, 145, '#ffd08a'], [287, 144, '#ffe6b0'], [291, 146, '#ffc070'], [295, 145, '#ffd890'], [270, 146, '#ffc878']]) px(x, lx, ly, col);
  for (let i = 360; i < PIL; i++) if (hash(i, 7, 23) < 0.3) px(x, i, HZ - 1, hash(i, 8, 23) < 0.5 ? '#d0a066' : '#8a7462');
  for (let i = 372; i < 440; i++) if (hash(i, 9, 23) < 0.14) px(x, i, HZ - 2, '#b08c62');

  // the far curve of beachfront hotels
  for (const b of HOTELS) {
    for (let y = b.top; y <= HZ - 4; y++) for (let i = b.x; i < b.x + b.w; i++) {
      const fromR = b.x + b.w - 1 - i;
      let col = ['#232640', '#292944', '#2e2c48', '#282e46', '#35344e'][Math.floor(b.tone * 5)];
      if (fromR < (b.w > 10 ? 2 : 1)) col = '#1a1c30';
      if (y === b.top) col = fromR < 2 ? '#2e3452' : '#3e4466';
      if (y > HZ - 11 && bayer(i, y) < (y - (HZ - 11)) / 9) col = fromR < 2 ? '#30262e' : '#43343e';
      px(x, i, y, col);
    }
    if (b.w > 8 && b.x > 0) {
      const tx = b.x + 2 + ((b.w * 7) % (b.w - 5));
      rect(x, tx, b.top - 2, 3, 2, '#1e2036'); px(x, tx, b.top - 2, '#3a3e5a');
      if (b.tone > 0.6) rect(x, b.x + b.w - 3, b.top - 4, 1, 4, '#2a2c44');
    }
  }
  for (const w of HWIN) px(x, w.x, w.y, !w.tog && w.lit ? w.col : '#171a2c');
  rect(x, SIGN.x - 1, SIGN.y - 1, 21, 7, '#140c18');
  rect(x, SIGN.x + 1, SIGN.y + 6, 1, 2, '#140c18'); rect(x, SIGN.x + 17, SIGN.y + 6, 1, 2, '#140c18');
  for (let i = 0; i < 186; i++) {
    if (lemeY(i) < HZ - 1) continue;
    px(x, i, HZ - 3, hash(i >> 1, 1, 21) < 0.55 ? (hash(i, 2, 21) < 0.5 ? '#d8944e' : '#a8683c') : '#2a2230');
    px(x, i, HZ - 2, i % 3 === 0 ? '#ffd080' : '#4a3634');
    px(x, i, HZ - 1, i % 3 === 1 ? '#8a7a70' : '#6a6068');
  }

  // cable car stations and the cable between the summits
  rect(x, 262, 108, 9, 3, '#2a3246'); rect(x, 262, 107, 9, 1, '#4a5470');
  px(x, 264, 109, '#ffcf80'); px(x, 266, 109, '#ffe0a0'); px(x, 268, 109, '#ffcf80');
  rect(x, 326, 69, 7, 4, '#2a3246'); rect(x, 326, 68, 7, 1, '#4a5470');
  px(x, 327, 70, '#ffcf80'); px(x, 329, 70, '#ffe0a0'); px(x, 331, 70, '#ffcf80');
  rect(x, 329, 66, 1, 2, '#3a4058');
  for (let k = 0; k <= 160; k++) {
    const s = k / 160, cx = Math.round(lerp(CAB.x0, CAB.x1, s)), cy = Math.round(cableY(s));
    px(x, cx, cy, (cx + cy) & 1 ? '#5c6884' : '#465070');
  }

  // the summit terrace under the statue
  rect(x, XC - 8, CORC, 15, 1, '#8a9cc4'); rect(x, XC - 8, CORC + 1, 15, 1, '#4c5c84');
  for (let i = XC - 8; i < XC + 7; i += 2) px(x, i, CORC - 1, '#6a7aa0');
  // Christ the Redeemer, floodlit white-blue
  const ox = XC - 11, oy = CORC - CHRIST.length;
  const at = (i, j) => j >= 0 && j < CHRIST.length && i >= 0 && i < 22 && CHRIST[j][i] === '#';
  for (let j = 0; j < CHRIST.length; j++) for (let i = 0; i < 22; i++) {
    if (!at(i, j)) continue;
    let col;
    if (j >= 19) col = j === 19 ? '#c0cbe4' : !at(i + 1, j) ? '#6c789a' : !at(i - 1, j) ? '#a8b4d0' : '#8e9aba';
    else if (j >= 3 && !at(i, j + 1)) col = '#93a4ce';
    else if (!at(i + 1, j)) col = '#b6c6ec';
    else if (!at(i - 1, j) || !at(i, j - 1)) col = '#ffffff';
    else col = '#e6eeff';
    px(x, ox + i, oy + j, col);
  }
  return c;
}

/* ================= Per-frame mountain life ================= */
const SIGN_BUZZ = [[61.2, 62.9], [143.5, 144.7], [212.2, 213.6]];
function drawMountainFx(t) {
  const s = step(t, 1200);
  for (const f of FAV) {                                            // favela lights: twinkle, some switch off a while
    if (!f.tw) continue;
    if (hash(f.id, Math.floor(frac(t / LOOP + f.off) * f.n), 41) < 0.2) continue;
    ctx.globalAlpha = hash(f.id, s, 42) < 0.07 ? 0.3 : 0.72 + 0.28 * wave(t, f.k, f.ph);
    rect(ctx, f.x, f.y, f.w, 1, f.col);
  }
  ctx.globalAlpha = 1;
  for (const w of HTOG) if (hash(w.id + 900, Math.floor(frac(t / LOOP + w.off) * w.n), 43) < w.p) px(ctx, w.x, w.y, w.col);
  // HOTEL sign on the nearest tower (buzzes now and then)
  let m = 0.94 + 0.06 * wave(t, 2400);
  if (SIGN_BUZZ.some(([a, b]) => t >= a && t < b)) { const hv = hash(5, Math.floor(t * 30)); m = hv > 0.45 ? 1 : hv > 0.2 ? 0.4 : 0; }
  if (m > 0) {
    text(ctx, 'HOTEL', SIGN.x, SIGN.y, m > 0.6 ? '#ff7a9e' : '#a8486a');
    halo(HL.sign, SIGN.x + 9, SIGN.y + 2, m);
  }
  // red aviation beacons
  for (let i = 0; i < BEACONS.length; i++) {
    const b = BEACONS[i];
    if (frac(t / 2 + i * 0.37) < 0.3) { px(ctx, b.x, b.y, '#ff4a4a'); halo(HL.red, b.x, b.y, 0.8); }
  }
  if (frac(t / 2 + 0.6) < 0.3) { px(ctx, 329, 65, '#ff4a4a'); halo(HL.red, 329, 65, 0.55); }
  halo(HL.station, 266, 109, 0.6); halo(HL.station, 329, 70, 0.6);
  // two cable-car cabins crossing between Urca and Sugarloaf (60 s round trip)
  const p = frac(t / 60);
  const sA = p < 0.15 ? 0 : p < 0.5 ? smooth(0.15, 0.5, p) : p < 0.65 ? 1 : 1 - smooth(0.65, 1, p);
  for (const sc of [sA, 1 - sA]) {
    const cx = Math.round(lerp(CAB.x0, CAB.x1, sc)), cy = Math.round(cableY(sc));
    halo(HL.cabin, cx, cy + 4, 0.55);
    rect(ctx, cx, cy + 1, 1, 1, '#3a3e52');
    rect(ctx, cx - 2, cy + 2, 5, 4, '#2a2e42');
    rect(ctx, cx - 1, cy + 2, 3, 1, '#9aa6c8');
    rect(ctx, cx - 1, cy + 3, 3, 2, '#ffdc98');
    px(ctx, cx, cy + 4, '#ffefc8');
  }
}

/* ================= The billboard on its beachfront hotel ================= */
const BBS = { x: BB_HOST.x0, y: BB.y - 1 };           // origin of the prerendered structure sprite
// the hotel's rooms: 2 x 3 px windows, 4 px apart, on its two upper floors; some stay lit or dark, the rest come
// and go like the far row's
const BB_WIN = [];
{
  const r = rng(58), { x0, x1, top, floor } = BB_HOST;
  for (let f = 0; f < 2; f++) for (let wx = x0 + 3; wx + 1 < x1 - 2; wx += 4) {
    const v = r();
    BB_WIN.push({ id: BB_WIN.length, x: wx, y: top + 2 + f * floor, tog: v < 0.3, lit: v > 0.7, n: 2 + Math.floor(r() * 7), p: 0.3 + r() * 0.4,
      off: r(), col: r() < 0.82 ? ['#d8b070', '#a8844e', '#7a5e3a'] : ['#a4c0d8', '#7e96ac', '#5a6c80'] });   // a lit room, top row to bottom
  }
}
const BB_WTOG = BB_WIN.filter(w => w.tog);
function bbWindow(c, w, ox = 0, oy = 0) { w.col.forEach((col, j) => rect(c, w.x - ox, w.y + j - oy, 2, 1, col)); }
function renderBillboardFrame() {                     // the hotel, the legs, the lamp rail and the frame (the lamps are drawn per frame)
  const { x0, x1, top, floor } = BB_HOST, [c, x] = makeCanvas(x1 - x0 + 1, HZ - BBS.y);
  const R = (gx, gy, w, h, col) => rect(x, gx - BBS.x, gy - BBS.y, w, h, col);
  R(x0, top, x1 - x0 + 1, HZ - top, '#2b2a46');                                   // walls
  R(x1 - 1, top, 2, HZ - top, '#1b1c31');                                         // the side in shadow
  R(x0, top, x1 - x0 - 1, 1, '#464c70'); R(x1 - 1, top, 2, 1, '#30365a');         // moonlit roof edge
  for (let y = top + floor; y < HZ - 1; y += floor) R(x0, y, x1 - x0 - 1, 1, '#35355a');   // balcony slabs
  for (const w of BB_WIN) { R(w.x, w.y, 2, 3, '#161828'); if (!w.tog && w.lit) bbWindow(x, w, BBS.x, BBS.y); }
  const gy = top + 2 * floor + 1;                                                 // ground floor: awning, a lit arcade, the pavement
  R(x0, gy, x1 - x0 - 1, 1, '#4a3a44');
  for (let i = x0 + 2, k = 0; i + 2 < x1 - 2; i += 5, k++) {
    if (hash(k, 5, 61) < 0.3) { R(i, gy + 1, 3, 3, '#1c1b30'); continue; }                 // a shutter down
    const low = hash(k, 6, 61) < 0.4;                                                       // some shops lit lower than others
    R(i, gy + 1, 3, 3, low ? '#86643e' : '#a47a4a'); R(i, gy + 1, 3, 1, low ? '#a47a4a' : '#c89a5e'); R(i + 1, gy + 3, 1, 1, '#5e4430');
  }
  R(x0, HZ - 1, x1 - x0 + 1, 1, '#5a5058');
  R(BB.x - 1, BB_DECK + 1, BB.w + 2, top - BB_DECK - 1, '#1a1a2f');               // the roof terrace's wall behind the legs
  for (const lx of BB_LEGS) { R(lx, BB_DECK + 1, 1, top - BB_DECK - 1, '#34364e'); R(lx + 1, BB_DECK + 1, 1, top - BB_DECK - 1, '#131425'); }
  R(BB.x - 1, BB_DECK, BB.w + 2, 1, '#2a2c44');                                   // lamp rail
  R(BB.x - 1, BB.y - 1, BB.w + 2, BB.h + 2, '#15162a');                           // frame (the face covers the inside)
  R(BB.x - 1, BB.y - 1, BB.w + 2, 1, '#3e4264');                                  // moonlit top edge
  R(BB.x + BB.w, BB.y, 1, BB.h, '#23263f');
  R(BB.x - 1, BB.y + BB.h, BB.w + 2, 1, '#2e2a3a');                               // bottom edge, warm from the lamps
  return c;
}
// the city's own ad: "AÇAÍ / NA PRAIA" and a bowl of açaí (P) with banana (Y), strawberry (R), granola (G), a spoon (w)
const ACAI_BOWL = ['........w..', '.......w...', '...YY..wR..', '..YyyY.wRr.', '.pPYYPPwPRP', 'PpPPPpPPPPP', 'PPPGgPPpPGP',
  'kbbbbbbbbbk', 'Bbbbbbbbbbd', '.BBbbbbbBd.', '..BBBBBBd..', '....BdB....'];
const ACAI_C = { P: '#5e2672', p: '#9a52ae', Y: '#fff0b0', y: '#e2c26a', R: '#ff4a5e', r: '#b82a44', G: '#e0a452', g: '#9a6632',
  w: '#e4ecf4', k: '#caa070', b: '#a8744a', B: '#7a4e34', d: '#4e3024' };
const BB_BOWL = { x: 30, y: 1 };
const BB_SPARK = [[33, 3], [37, 3], [31, 6], [38, 6]];   // face-local spots where a sparkle can pop
// the night's light on the painted panel: its colours stepped down and pulled towards the dusk behind the hills, the
// paint run and scuffed by the sea air (gradeAd for the prerendered ad, bbTone for what is painted over it per frame)
const BB_GRADE = { level: 0.76, tint: '#231a44', mix: 0.12, wear: 0.4, seed: 7 };
const bbTone = (col, level = BB_GRADE.level, mix = BB_GRADE.mix) => { const T = hex(BB_GRADE.tint); return '#' + hex(col).map((v, k) => Math.round(lerp(v * level, T[k], mix)).toString(16).padStart(2, '0')).join(''); };
const bbInk = col => bbTone(col, 0.86, 0.08);          // the lettering is the freshest paint: it carries the lamps' light
function bigText(c, s, x, y, col) {                   // FONT3 at 2x (every font pixel is a 2x2 block), letters 1 px apart
  let cx = x;
  for (const ch of s) {
    FONT3[ch].forEach((row, j) => { for (let i = 0; i < 3; i++) if (row[i] === '1') rect(c, cx + 2 * i, y + 2 * j, 2, 2, col); });
    cx += 7;
  }
}
function renderBillboardAd(lit) {                     // lit[k]: floodlamp k on (its pool of light on the face)
  const { w, h } = BB, lamps = BB_LAMPS.map(v => v - BB.x + 1);
  const c = ditherFill(w, h, ['#08222e', '#0b2f3a', '#0f3e48', '#145058', '#1a6468', '#227878', '#2e8e88'], (i, j) => {
    let v = 0.1 + 0.36 * j / (h - 1);
    lamps.forEach((l, k) => { const d = Math.hypot((i - l) / 12, (j - h) / 15); if (lit[k] && d < 1) v += 0.5 * (1 - d) ** 1.2; });
    return v;
  });
  const x = c.getContext('2d');
  gradeAd(c, BB_GRADE);                               // the board weathers; the letters and the bowl are painted over it
  bigText(x, 'AÇAÍ', 2, 2, bbTone('#04161e')); bigText(x, 'AÇAÍ', 1, 1, bbInk('#f6dfb4'));
  text(x, 'NA PRAIA', 2, 13, bbTone('#04161e')); text(x, 'NA PRAIA', 1, 12, bbInk('#ffd87a'));
  ACAI_BOWL.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (ACAI_C[row[i]]) px(x, BB_BOWL.x + i, BB_BOWL.y + j, bbTone(ACAI_C[row[i]])); });
  return c;
}
function renderBillboardGlint() {                     // the AÇAÍ letters, brighter, for the glint sweeping across them
  const [c, x] = makeCanvas(BB.w, BB.h);
  bigText(x, 'AÇAÍ', 1, 1, bbTone('#ffffff'));
  return c;
}
function bbLampLevels(t) {
  let mid = 1;
  if (BB_FLICK.some(([a, b]) => t >= a && t < b)) { const hv = hash(8, Math.floor(t * 24), 3); mid = hv > 0.55 ? 1 : hv > 0.3 ? 0.3 : 0; }
  return [0.94 + 0.06 * wave(t, 1500), mid * (0.94 + 0.06 * wave(t, 1720, 0.3)), 0.94 + 0.06 * wave(t, 1330, 0.6)];
}
const BB_SPARK_COL = bbTone('#ffffff');
function drawBillboard(t, pitch) {
  const L = bbLampLevels(t);
  ctx.drawImage(BBF_C, BBS.x, BBS.y);
  for (const w of BB_WTOG) if (hash(w.id + 700, Math.floor(frac(t / LOOP + w.off) * w.n), 43) < w.p) bbWindow(ctx, w);
  halo(HL.bbWash, BB.x + (BB.w >> 1), BB.y + BB.h, 0.55 + 0.15 * (L[0] + L[1] + L[2]));   // light spilling around the face
  if (!PITCH.covers(t, pitch)) {
    ctx.drawImage(L[1] > 0.5 ? BB_AD[0] : BB_AD[1], BB.x, BB.y);
    const g = frac(t / 12);                           // a glint sweeps across the letters every 12 s
    if (g < 0.075) {
      const sx = Math.round(lerp(-4, 30, g / 0.075));
      for (let j = 0; j < 10; j++) {                  // a 3 px band leaning like "/", brightest in the middle
        const gx = sx - (j >> 1);
        for (let k = 0; k < 3; k++) {
          if (gx + k < 1 || gx + k > 27) continue;
          ctx.globalAlpha = k === 1 ? 1 : 0.5;
          ctx.drawImage(BB_GLINT, gx + k, 1 + j, 1, 1, BB.x + gx + k, BB.y + 1 + j, 1, 1);
        }
      }
      ctx.globalAlpha = 1;
    }
    const { cyc, u } = cycle(t, 60);                  // a sparkle pops on the bowl every 4 s
    if (u < 0.3) {
      const [sx, sy] = BB_SPARK[Math.floor(hash(cyc, 5, 88) * BB_SPARK.length)], X = BB.x + sx, Y = BB.y + sy;
      px(ctx, X, Y, BB_SPARK_COL);
      if (u > 0.06 && u < 0.21) { ctx.globalAlpha = 0.7; rect(ctx, X - 1, Y, 3, 1, BB_SPARK_COL); rect(ctx, X, Y - 1, 1, 3, BB_SPARK_COL); ctx.globalAlpha = 1; }
    }
  }
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  BB_LAMPS.forEach((lx, k) => {                       // floodlamp heads on the rail, with a little glare
    rect(ctx, lx, BB_DECK, 2, 1, L[k] > 0.5 ? '#c4b48e' : L[k] > 0.1 ? '#94805e' : '#5a4c4a');
    halo(HL.bbLamp, lx + 1, BB_DECK, L[k]);
  });
}

/* ================= The bay ================= */
const WATER_P = ['#2a6c7a', '#22606f', '#1b5264', '#164659', '#113a4e', '#0d2f44', '#0a263a'].map(hex);
function renderWater() {
  const h = H - HZ, [c, x] = makeCanvas(W, h);
  const img = x.createImageData(W, h), d = img.data;
  for (let y = 0; y < h; y++) for (let i = 0; i < W; i++) {
    let v = Math.pow(Math.min(1, y / 46), 0.75);
    v -= Math.max(0, 1 - Math.abs(i - MOON.x) / 90) * 0.18 * (1 - v);        // brighter water under the moon
    const f = clamp(v, 0, 1) * (WATER_P.length - 1);
    let k = Math.floor(f);
    if (f - k > bayer(i, y)) k++;
    const col = WATER_P[Math.min(k, WATER_P.length - 1)], o = (y * W + i) * 4;
    d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2]; d[o + 3] = 255;
  }
  x.putImageData(img, 0, 0);
  return c;
}
const WSTREAKS = [
  { x: SIGN.x + 9, w: 3, col: '#ff7aa0', a: 0.28, seed: 1 }, { x: 40, w: 3, col: '#ffc070', a: 0.2, seed: 2 },
  { x: 92, w: 2, col: '#ffc070', a: 0.18, seed: 3 }, { x: 140, w: 2, col: '#ffcf80', a: 0.18, seed: 4 },
  { x: 241, w: 2, col: '#ffd890', a: 0.22, seed: 5 },
];
function drawMoonPath(t) {
  const y1 = shoreY(MOON.x) - 1;
  streak(ctx, t, MOON.x, 11, '#8fdcd2', 0.4, 3.1, HZ, y1);
  streak(ctx, t, MOON.x, 5, '#fff2cc', 0.85, 7.7, HZ, y1);
  const s = step(t, 1440);
  for (let j = 0; j < 30; j++) {
    if (hash(j, s, 51) > 0.5) continue;
    const k = Math.floor(Math.pow(hash(j, s, 52), 0.8) * (y1 - HZ)), y = HZ + k;
    const spread = 2 + k * 0.42;
    ctx.globalAlpha = 0.95 - k / 90;
    px(ctx, Math.round(MOON.x + (hash(j, s, 53) - 0.5) * 2 * spread), y, hash(j, s, 54) < 0.5 ? '#ffffff' : '#fff0c0');
  }
  ctx.globalAlpha = 1;
}
const BOAT_Y = 150;                                   // the cruiser's track: well out past the surf (swells start ~24 px off the sand)
function renderBoat() {                               // small cabin cruiser heading left (18 x 10)
  const [c, x] = makeCanvas(18, 10);
  rect(x, 9, 0, 1, 4, '#4a4a60');
  rect(x, 5, 4, 8, 2, '#c8ccd8'); rect(x, 5, 4, 8, 1, '#e8ecf4');
  px(x, 6, 5, '#ffd890'); px(x, 8, 5, '#ffe6a8'); px(x, 10, 5, '#ffd890');
  rect(x, 1, 6, 16, 1, '#dde0ea'); px(x, 0, 6, '#dde0ea');
  rect(x, 1, 7, 16, 1, '#8a90a4'); rect(x, 2, 8, 14, 1, '#4a4e64'); rect(x, 4, 9, 10, 1, '#2a2e44');
  return c;
}
function drawBoat(t) {
  const tau = t % 120;
  if (tau < 6 || tau >= 112) return;
  const u = (tau - 6) / 106, bx = Math.round(lerp(474, 150, u)), by = BOAT_Y;
  for (let j = 0; j < 7; j++) {                       // rippled reflection
    const off = Math.round(Math.sin(TAU * 45 * t / LOOP + j * 0.9) * (0.5 + j * 0.2));
    ctx.globalAlpha = 0.3 * (1 - j / 7);
    ctx.drawImage(BOAT_C, 0, 9 - j, 18, 1, bx + off, by + 10 + j, 18, 1);
  }
  ctx.globalAlpha = 0.5; ctx.fillStyle = '#cfe8ee';   // wake trailing behind (to the right)
  for (let j = 1; j < 14; j++) if (hash(j, step(t, 480), 57) < 0.7) ctx.fillRect(bx + 17 + j, by + 9 + (j >> 2), 1 + (j & 1), 1);
  ctx.globalAlpha = 1;
  ctx.drawImage(BOAT_C, bx, by);
  px(ctx, bx + 9, by, '#ffffff'); px(ctx, bx + 1, by + 7, '#ff4a4a');
  halo(HL.boat, bx + 9, by + 4, 0.8); halo(HL.red, bx + 1, by + 7, 0.6);
  streak(ctx, t, bx + 8, 3, '#ffd890', 0.35, 9, by + 11, by + 24);
}

/* ================= Fireworks over the bay (two shows per loop) ================= */
const FW_COLS = [
  ['#fffbe8', '#ffd24a', '#ff8a2a'], ['#f0fffc', '#5cf2d8', '#1f9a8e'], ['#fff0fa', '#ff6ab4', '#b02a70'],
  ['#ffffff', '#c0ccff', '#7a6aff'], ['#f6ffe8', '#9cff6a', '#2a9a44'], ['#fff6f0', '#ff6a4a', '#b02a2a'],
];
const FW_LIFE = [2.8, 2.4, 4.0];                      // peony, ring, gold willow
const RISE = 1.3;                                     // rocket climb before each burst
function fwDirs(b, id) {                              // unit launch directions for every star of a shell
  const d = new Float32Array(b.n * 2), rot = hash(id, 2, 60) * TAU;
  for (let i = 0; i < b.n; i++) {
    if (b.kind === 1) {                               // tilted ring with an inner ring of hot white stars
      const a = TAU * i / b.n + rot, rr = i % 3 === 0 ? 0.5 : 1;
      d[2 * i] = Math.cos(a) * rr; d[2 * i + 1] = Math.sin(a) * 0.84 * rr;
    } else {                                          // stars on a sphere, projected: filled core, bright rim
      const z = 1 - 2 * (i + 0.5) / b.n, rr = Math.sqrt(1 - z * z), a = i * 2.39996 + rot, sp = 0.9 + 0.1 * hash(id, i, 62);
      d[2 * i] = Math.cos(a) * rr * sp; d[2 * i + 1] = Math.sin(a) * rr * sp;
    }
  }
  return d;
}
const BURSTS = [
  { t: 78.2, x: 256, y: 60, c: 0, kind: 0, n: 96, R: 38 }, { t: 79.6, x: 344, y: 46, c: 1, kind: 1, n: 96, R: 30 },
  { t: 80.8, x: 214, y: 84, c: 2, kind: 0, n: 80, R: 30 }, { t: 82.0, x: 298, y: 40, c: 0, kind: 2, n: 96, R: 42 },
  { t: 83.6, x: 250, y: 62, c: 3, kind: 0, n: 90, R: 36 }, { t: 83.9, x: 342, y: 74, c: 5, kind: 0, n: 70, R: 28 },
  { t: 197.4, x: 322, y: 54, c: 2, kind: 0, n: 96, R: 38 }, { t: 198.8, x: 232, y: 72, c: 4, kind: 1, n: 96, R: 30 },
  { t: 200.1, x: 280, y: 38, c: 0, kind: 2, n: 96, R: 42 }, { t: 201.5, x: 354, y: 76, c: 1, kind: 0, n: 80, R: 30 },
  { t: 203.0, x: 262, y: 54, c: 3, kind: 0, n: 90, R: 36 }, { t: 203.2, x: 330, y: 62, c: 5, kind: 1, n: 84, R: 26 },
].map((b, id) => ({ ...b, id, lx: b.x + Math.round((hash(id, 1, 60) - 0.5) * 16), dirs: fwDirs(b, id) }));
function fwReflect(x, y, a, t) {                      // a dim, rippled copy of a spark in the bay
  const ry = HZ + (HZ - y) * 0.36;
  if (y >= HZ || ry >= shoreY(x) - 2) return;
  ctx.globalAlpha = a;
  ctx.fillRect(Math.round(x + Math.sin(ry * 1.7 + TAU * 300 * t / LOOP) * 1.2), Math.round(ry), 1, 1);
}
function drawFireworks(t) {
  const sp = step(t, 2400);
  for (const b of BURSTS) {
    const tau = t - b.t, life = FW_LIFE[b.kind];
    if (tau < -RISE || tau > life) continue;
    if (tau < 0) {                                    // rocket climbing from a barge in the bay
      const u = (tau + RISE) / RISE;
      for (let k = 7; k >= 0; k--) {
        const uu = u - k * 0.03;
        if (uu < 0) continue;
        const e = 1 - (1 - uu) * (1 - uu), x = lerp(b.lx, b.x, uu) + (k ? Math.sin(k * 2.1 + b.id) * 0.6 : 0), y = lerp(176, b.y, e) + k * 0.4;
        ctx.globalAlpha = k ? 0.6 * (1 - k / 8) : 1;
        ctx.fillStyle = k ? (k < 3 ? '#ffd890' : '#ff8a3a') : '#fff6d8';
        ctx.fillRect(Math.round(x), Math.round(y), 1, 1);
        if (!k) fwReflect(x, y, 0.4, t);
      }
      continue;
    }
    const pal = FW_COLS[b.c], u = tau / life, willow = b.kind === 2, k = willow ? 1.9 : 2.8, g = willow ? 9 : 5;
    if (tau < 0.35) { const f = 1 - tau / 0.35; halo(HL.flash, b.x, b.y, f); halo(HL.flashW, b.x, HZ + (HZ - b.y) * 0.36, f * 0.7); }
    halo(HL.fwGlow[b.c], b.x, b.y + g * tau * tau * 0.6, 0.65 * (1 - u));
    halo(HL.fwGlowW[b.c], b.x, HZ + (HZ - b.y) * 0.36, 0.45 * (1 - u));
    const fade = u < 0.5 ? 1 : 1 - (u - 0.5) / 0.5;
    const head = u < 0.1 ? pal[0] : u < 0.6 ? pal[1] : pal[2];
    const trail = willow ? (u < 0.5 ? '#ffc860' : '#c87a30') : pal[2];
    const nT = willow ? 7 : 3, dT = willow ? 0.1 : 0.06;
    const r0 = b.R * (1 - Math.exp(-k * tau)), dy0 = g * tau * tau;
    for (let i = 0; i < b.n; i++) {
      const dx = b.dirs[2 * i], dy = b.dirs[2 * i + 1];
      let a = fade;
      if (u > 0.45 && hash(b.id * 131 + i, sp, 63) < 0.35) a *= 0.2;       // crackle as the stars die
      ctx.fillStyle = trail;
      for (let j = nT; j >= 1; j--) {                 // sparkle trail
        const tt = tau - j * dT;
        if (tt <= 0) continue;
        const r = b.R * (1 - Math.exp(-k * tt));
        ctx.globalAlpha = a * 0.55 * (1 - j / (nT + 1));
        ctx.fillRect(Math.round(b.x + dx * r), Math.round(b.y + dy * r + g * tt * tt), 1, 1);
      }
      const x = b.x + dx * r0, y = b.y + dy * r0 + dy0;
      ctx.fillStyle = b.kind === 1 && i % 3 === 0 ? (u < 0.55 ? '#ffffff' : pal[1]) : head; ctx.globalAlpha = a;
      ctx.fillRect(Math.round(x), Math.round(y), 1, 1);
      if (u < 0.25) { ctx.globalAlpha = a * 0.6; ctx.fillRect(Math.round(x + dx), Math.round(y + dy), 1, 1); }
      fwReflect(x, y, a * 0.5, t);
    }
  }
  ctx.globalAlpha = 1;
}

/* ================= Sky visitors: a plane into Santos Dumont, shooting stars ================= */
function drawPlane(t) {
  if (t < 96 || t >= 146) return;
  const u = (t - 96) / 50, x = lerp(500, 268, u), y = lerp(16, 106, Math.pow(u, 1.25));
  rect(ctx, Math.round(x) - 1, Math.round(y), 4, 1, '#2a2a3a');
  halo(HL.landing, x - 2, y, 0.85);
  px(ctx, Math.round(x) - 2, Math.round(y), '#ffffff');
  if (frac(t) < 0.12) { px(ctx, Math.round(x) + 1, Math.round(y) - 1, '#ff4040'); halo(HL.red, x + 1, y - 1, 0.7); }
  const f = frac(t / 1.5);
  if (f < 0.05 || (f > 0.12 && f < 0.17)) { px(ctx, Math.round(x) + 2, Math.round(y), '#ffffff'); halo(HL.strobe, x + 2, y, 0.8); }
}
const SHOOTS = [{ t: 31.5, x: 110, y: 12, dx: -70, dy: 28 }, { t: 152.3, x: 300, y: 8, dx: -80, dy: 30 }];
function drawShootingStars(t) {
  for (const s of SHOOTS) {
    const u = (t - s.t) / 1.1;
    if (u < 0 || u > 1) continue;
    const a = Math.sin(Math.PI * u);
    for (let k = 0; k < 18; k++) {
      const uu = u - k * 0.018;
      if (uu < 0) break;
      ctx.globalAlpha = a * (1 - k / 18);
      px(ctx, Math.round(s.x + s.dx * uu), Math.round(s.y + s.dy * uu), k < 2 ? '#ffffff' : k < 7 ? '#dff6ff' : '#9fdcf0');
    }
    halo(HL.strobe, s.x + s.dx * u, s.y + s.dy * u, a * 0.7);
  }
  ctx.globalAlpha = 1;
}

/* ================= Copacabana: sand, calçadão, avenue (static) ================= */
const LAMPS = [34, 142, 228, 408];
const KIOSKS = [{ x: 66, sign: 'COCO', col: '#8affe4' }, { x: 192, sign: 'BAR', col: '#ff94c0' }, { x: 330, sign: 'SUCO', col: '#ffd884' }];
const PALMS = [
  { x: 16, h: 46, lean: 6 }, { x: 100, h: 55, lean: -5 }, { x: 160, h: 40, lean: 4 },
  { x: 244, h: 50, lean: -6 }, { x: 290, h: 42, lean: 4 }, { x: 362, h: 53, lean: -4 },
].map((p, i) => ({ ...p, i, k: 14 + i * 3, k2: 53 + i * 7, ph: i * 0.23, base: promY(p.x) - 1 }));
const PAVE_P = 72, PAVE_A = 2.2, PAVE_B = 3;          // calçadão waves: one every 72 px, swinging ±2.2 rows, 3-row bands
const SAND = ['#283044', '#30384a', '#3c3e4e', '#4a4652', '#5a5056', '#6a5a58', '#7c665c', '#8e7260', '#a07e64',
  '#b08a6a', '#c09672'].map(hex);
function beachLight(x, y) {
  let v = 0;
  for (const lx of LAMPS) { const d = Math.hypot((x - lx) / 46, (y - promY(lx) - 5) / 17); if (d < 1) v = Math.max(v, (1 - d) ** 1.4); }
  for (const k of KIOSKS) { const d = Math.hypot((x - k.x) / 36, (y - promY(k.x) + 4) / 13); if (d < 1) v = Math.max(v, Math.min(1, (1 - d) ** 1.2 * 1.15)); }
  return Math.min(1, v);
}
// Copacabana's round kiosks: a broad shell-like canopy (a pale cone on a flared teal brim) overhanging a curved bar
const K_BRIM = 16, K_TABLE = 19, K_POLE = 23;         // canopy half-width; café tables and string-light poles (px from the centre)
const K_SIGN = 20;                                    // rows above the kiosk's base: middle of the roof sign on the canopy's apex
const kWire = (k, s, u) => [Math.round(lerp(k.x + s * (K_BRIM - 1), k.x + s * K_POLE, u)),
  Math.round(lerp(promY(k.x) - 11, promY(k.x) - 13, u) + 8 * u * (1 - u))];   // string lights: brim tip -> pole top, sagging
const KBULBS = [];                                    // string-light bulbs of the kiosks
for (const k of KIOSKS) {
  for (const s of [-1, 1]) {
    for (let j = 1; j <= 4; j++) {
      const [x, y] = kWire(k, s, j / 5);
      KBULBS.push({ id: KBULBS.length, x, y,
        col: ['#ffd27a', '#fff0c0', '#ff9a5a', '#ffd27a', '#8affe0'][(j + (s > 0 ? 2 : 0)) % 5], k: 30 + j * 11, ph: hash(KBULBS.length, 3) });
    }
  }
}
function renderBeach() {
  const [c, x] = makeCanvas(W, H);
  const img = x.createImageData(W, H), d = img.data;
  const put = (i, y, col) => { const o = (y * W + i) * 4; d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2]; d[o + 3] = 255; };
  const WHITE = ['#8c867e', '#aca498', '#cdc4b4', '#e6dccb'].map(hex), BLACK = ['#16141b', '#1d1a21', '#26222a', '#302a2e'].map(hex);
  const ASPH = ['#08070d', '#0d0b13', '#121019', '#18141e', '#201a22', '#2a2026'].map(hex);
  for (let i = 0; i < W; i++) {
    const sy = shoreY(i), py = promY(i), ay = py + PROM_H;
    for (let y = sy; y < H; y++) {
      const L = beachLight(i, y), b = bayer(i, y);
      let col;
      if (y < py) {                                   // sand: cool and wet by the water, warm under the lamps
        const s = (y - sy) / (py - sy);
        let v = 0.12 + 0.5 * s + 0.5 * L * (0.4 + 0.6 * s);
        if (y - sy < 3) v = 0.02 + 0.07 * (y - sy) + 0.2 * L;
        if (hash(i, y, 71) < 0.07) v -= 0.1;
        const f = clamp(v, 0, 1) * (SAND.length - 1);
        let k = Math.floor(f); if (f - k > b) k++;
        col = SAND[Math.min(k, SAND.length - 1)];
      } else if (y < ay) {                            // calçadão: Burle Marx's big lazy waves, parallel to the beach
        const white = (Math.floor((y - shoreF(i) - SAND_H + PAVE_A * Math.sin(TAU * (i - 20) / PAVE_P) + 20) / PAVE_B) & 1) === 0;
        const f = clamp(0.22 + L * 0.95, 0, 1) * 3;
        let k = Math.floor(f); if (f - k > b) k++;
        col = (white ? WHITE : BLACK)[Math.min(k, 3)];
      } else if (y === ay) col = HX(L > 0.3 ? '#8a7e80' : '#5a5260');                           // curb
      else {                                          // Avenida Atlântica, fading into the dark below the balcony
        const lane = y - ay, depth = lane / (H - ay);
        const f = clamp(0.5 - depth * 0.55 + L * 0.55, 0, 1) * (ASPH.length - 1);
        let k = Math.floor(f); if (f - k > b) k++;
        col = ASPH[Math.min(k, ASPH.length - 1)];
        if ((lane === 6 || lane === 20) && ((i >> 2) & 1)) col = HX(L > 0.2 ? '#4a4450' : '#2c2834');
        if (lane === 12) col = HX(hash(i >> 1, 3, 72) < 0.5 ? '#101c16' : '#0c1612');
        if (lane === 28) col = HX('#3a3440');
      }
      put(i, y, col);
    }
  }
  x.putImageData(img, 0, 0);
  return { base: c, props: renderBeachProps() };
}
const K_SLAT = ['#a86c42', '#6e4028', '#c88a5a'];     // the bar's slatted front, bottom row up
const K_SHIRTS = [['#e8dcc0', '#3a8a8a'], ['#d8584a', '#e8c050'], ['#4a86c8', '#e07ab0']];
const K_HAIRS = [['#1a1214', '#6a4422'], ['#3a2418', '#c8a060'], ['#1a1214', '#3a2418']];
function drawKiosk(x, k, ki) {                        // one kiosk; n counts rows up from its base (the last row of sand)
  const cx = k.x, base = promY(cx) - 1, P = (dx, n, col) => px(x, cx + dx, base - n, col);
  for (const s of [-1, 1]) {                          // café tables with closed umbrellas and a seated customer, light poles
    const tx = cx + s * K_TABLE;
    rect(x, tx - 1, base - 2, 3, 1, '#5a4a40'); px(x, tx, base - 1, '#2a2024');
    rect(x, tx, base - 8, 1, 6, '#7a6a5e'); rect(x, tx - 1, base - 8, 3, 3, s < 0 ? '#c8524a' : '#e2d4b4');
    px(x, tx - 2 * s, base - 3, '#2a1c20'); px(x, tx - 2 * s, base - 2, '#3a4a6a');
    rect(x, cx + s * K_POLE, base - 12, 1, 12, '#3a3038');
  }
  for (let dx = -9; dx <= 9; dx++) {                  // the curved bar: lit wooden top, slatted front, ends turning away
    const d = Math.abs(dx), top = d <= 7 ? 3 : 4, bot = d <= 7 ? 0 : 1;
    for (let n = bot; n <= top; n++) {
      let col = n === top ? (d === 9 ? '#b07c50' : '#f0cc98') : K_SLAT[n - bot];
      if (n < top && d === 9) col = '#4e2c20'; else if (n < top && d === 8) col = n - bot === 1 ? '#5a3424' : '#8a5634';
      P(dx, n, col);
    }
  }
  for (let dx = -7; dx <= 7; dx++) for (let n = 4; n <= 8; n++) {   // the round cabin: lit menu fascia, bright interior
    const d = Math.abs(dx);
    let col = n === 8 ? '#ffe0a0' : n === 7 ? (dx === -4 || dx === 3 ? '#a86a38' : '#ffc672') : '#e8a458';
    if (d === 7) col = n >= 7 ? '#b07840' : '#3a2a2a';
    P(dx, n, col);
  }
  for (const [bx, col] of [[-5, '#3a8a5a'], [-3, '#c8423a'], [-1, '#e8c050'], [2, '#5a8ae0'], [4, '#3a8a5a']]) P(bx, 6, col);   // bottles
  P(-2, 5, '#3a2420'); P(-2, 4, '#5a3020');                                                        // barman
  const brimTop = d => d <= 13 ? 11 : 10, coneTop = d => 17 - Math.round(d * 0.55);
  for (let dx = -K_BRIM - 1; dx <= K_BRIM + 1; dx++) {   // a dark line along the canopy's top, so it reads against the surf
    const d = Math.abs(dx);
    P(dx, d > K_BRIM ? 10 : Math.max(brimTop(d), d <= 9 ? coneTop(d) : 0) + 1, '#101824');
  }
  for (let dx = -K_BRIM; dx <= K_BRIM; dx++) {        // the flared brim: lit white edge curving toward us, teal above
    const d = Math.abs(dx), top = brimTop(d), bot = d <= 11 ? 9 : 10;
    for (let n = bot; n <= top; n++) P(dx, n, n === bot ? (d >= 15 ? '#9cc8c4' : '#eef4f2') : n === top ? (dx > 3 ? '#5ab8ae' : '#3aa29a') : '#1f8a88');
    if (d >= 8 && d <= 14) P(dx, bot - 1, d >= 13 ? '#0c3436' : '#12484a');                      // shadowed underside
  }
  for (let dx = -9; dx <= 9; dx++) {                  // the cone rising to the sign, moonlit on the right
    const top = coneTop(Math.abs(dx));
    for (let n = 12; n <= top; n++) {
      const b = bayer(cx + dx, base - n);
      let col = dx > 2 ? (b < 0.6 ? '#98a6b0' : '#86949e') : dx < -2 ? (b < 0.5 ? '#5a6676' : '#687484') : '#7a8896';
      if (n === top) col = dx >= 0 ? '#c4d0d6' : '#8a96a4';
      if (n === 12) col = dx > 0 ? '#56626e' : '#46505e';                                        // shade on the brim
      P(dx, n, col);
    }
  }
  const tw = textWidth(k.sign), sx = cx - (tw >> 1);  // the glowing roof sign on the apex
  rect(x, sx - 1, base - K_SIGN - 3, tw + 2, 7, '#0a1016'); rect(x, sx - 1, base - K_SIGN - 3, tw + 2, 1, '#1e2a34');
  text(x, k.sign, sx, base - K_SIGN - 2, k.col);
  K_SHIRTS[ki].forEach((shirt, i) => {                // two customers standing at the bar under the brim
    const s = i ? 1 : -1, X = cx + (s < 0 ? -12 : 11), dir = -s;
    rect(x, X, base - 6, 2, 2, K_HAIRS[ki][i]); px(x, X + (dir > 0 ? 1 : 0), base - 5, '#c08a68');
    rect(x, X, base - 4, 2, 2, shirt); rect(x, X, base - 2, 2, 1, '#2a2a3c'); rect(x, X, base - 1, 2, 2, '#4a3428');
    px(x, X + (dir > 0 ? 1 : 0), base - 4, '#f0b070');                                            // lit by the bar
  });
}
function renderBeachProps() {                         // everything standing on the beach (drawn over the surf)
  const [c, x] = makeCanvas(W, H);
  for (const k of KIOSKS) for (const s of [-1, 1]) for (let j = 0; j <= 12; j++) {   // string-light wires: brim tips -> poles
    const [wx, wy] = kWire(k, s, j / 12);
    px(x, wx, wy - 1, '#2a2226');
  }
  // lifeguard post and a volleyball net on the sand
  { const cx = 120, bs = promY(cx) - 3;
    rect(x, cx - 3, bs - 6, 1, 7, '#7a7a84'); rect(x, cx + 3, bs - 6, 1, 7, '#7a7a84');
    rect(x, cx - 4, bs - 11, 9, 5, '#cfd4da'); rect(x, cx - 3, bs - 10, 7, 2, '#ffd890'); rect(x, cx + 3, bs - 11, 1, 5, '#8a909c');
    rect(x, cx - 5, bs - 12, 11, 1, '#d8483a'); rect(x, cx, bs - 17, 1, 5, '#6a6a74'); rect(x, cx + 1, bs - 17, 3, 2, '#e8c040'); }
  { const nb = promY(262) - 5;
    rect(x, 254, nb - 7, 1, 8, '#8a8a94'); rect(x, 270, nb - 7, 1, 8, '#8a8a94');
    for (let i = 255; i < 270; i++) { px(x, i, nb - 7, '#d0d0d6'); if (i & 1) px(x, i, nb - 5, '#6a6a74'); px(x, i, nb - 4, '#8a8a94'); } }
  // couples sitting on the sand, watching the sea, their heads leaning together
  for (const [cx, off, tops, hairs] of [[150, 7, ['#6a7488', '#a8566a'], ['#1a1214', '#3a2418']], [222, 6, ['#c8b89a', '#4a6a8a'], ['#6a4422', '#1a1214']],
    [106, 8, ['#8a5a8a', '#5a7a6a'], ['#c8a060', '#3a2418']]]) {
    const y = shoreY(cx) + off;
    for (let k = 0; k < 2; k++) {
      const bx = cx + 4 * k;
      rect(x, bx, y - 3, 4, 3, tops[k]); rect(x, bx, y - 1, 4, 1, '#2a2430');                 // shoulders and back, in the sand's shadow
      rect(x, bx + 2 - 2 * k, y - 5, 2, 2, hairs[k]); px(x, bx + 3 - 2 * k, y - 5, '#8a8aa8');  // head, moonlit on top
    }
  }
  KIOSKS.forEach((k, ki) => drawKiosk(x, k, ki));
  // palm trunks (the crowns sway, see renderPalmCrown)
  for (const p of PALMS) {
    for (let j = 0; j <= p.h; j++) {
      const v = j / p.h, tx = Math.round(p.x + p.lean * Math.pow(v, 1.6)), y = p.base - j;
      const ring = j % 3 === 0, lit = Math.max(0, 1 - v * 2.2);
      rect(x, tx, y, 2, 1, ring ? '#3e302e' : '#2a2024');
      if (lit > bayer(tx, y) * 0.8) px(x, tx, y, ring ? '#8a6448' : '#6a4c3a');
    }
  }
  // lamp posts along the calçadão
  for (const lx of LAMPS) {
    const by = promY(lx) + PROM_H;
    rect(x, lx, by - 16, 1, 16, '#2a2630'); rect(x, lx - 1, by - 1, 3, 1, '#3a3440');
    rect(x, lx - 1, by - 18, 3, 2, '#fff0c8'); px(x, lx, by - 19, '#3a3440');
  }
  return c;
}

/* ================= Palm crowns (pre-rendered sway frames) ================= */
const PALM_NF = 9, PALM_OX = 26, PALM_OY = 13;
function renderPalmCrown(p, a) {
  const w = 52, h = 36, [c, x] = makeCanvas(w, h), r = rng(500 + p.i);
  const angs = [166, 196, 226, 258, 290, 322, 352, 18, 138];
  const pts = [];
  angs.forEach((deg, fi) => {
    const th0 = (deg + (r() - 0.5) * 14) * Math.PI / 180, L = 11 + r() * 6;
    const ch = Math.cos(th0), th = th0 + a * (0.1 + 0.06 * Math.abs(ch)) + a * 0.04 * fi / angs.length;
    const droop = 0.42 + 0.4 * Math.abs(ch) + a * 0.06 * Math.sign(ch);
    let lx = 0, ly = 0;
    for (let s = 0; s <= 1.0001; s += 0.06) {
      const X = Math.cos(th) * L * s, Y = Math.sin(th) * L * s + droop * L * s * s;
      const gx = Math.round(PALM_OX + X), gy = Math.round(PALM_OY + Y);
      if (gx === lx && gy === ly) continue;
      lx = gx; ly = gy;
      pts.push([gx, gy, 3]);                                                 // rachis
      if (s > 0.15) {
        const len = Math.round(1 + 2.4 * Math.sin(Math.PI * Math.min(1, s * 1.1)));
        const side = Math.cos(th) > 0 ? 1 : -1;
        for (let k = 1; k <= len; k++) pts.push([gx + (k > 2 ? side : 0), gy + k, k === len ? 1 : 2]);   // hanging leaflets
        if (s > 0.3) pts.push([gx, gy - 1, 4]);                               // upper leaflets catch the moon
      }
    }
  });
  const COL = ['', '#1c3e32', '#0e241e', '#244a3a', '#3a6a5c'];
  for (const [gx, gy, k] of pts) if (gx >= 0 && gy >= 0 && gx < w && gy < h) px(x, gx, gy, COL[k]);
  rect(x, PALM_OX - 1, PALM_OY - 1, 3, 3, '#1a2a22');
  px(x, PALM_OX - 1, PALM_OY + 1, '#3a2a1a'); px(x, PALM_OX + 1, PALM_OY + 1, '#4a3420'); px(x, PALM_OX, PALM_OY + 2, '#3a2a1a');  // coconuts
  return c;
}

/* ================= Wave sets rolling onto the beach ================= */
const WAVE_P = 8, WAVE_LIFE = 18;                     // a new wave every 8 s (30 per loop), each lives 18 s
const SET_AMP = [1, 0.85, 0.7, 0.4, 0.34, 0.46];     // sets: three bigger waves, then three small ones
function waveDist(u, big) {                           // front distance above the shoreline (px); < 0 = run-up on the sand
  const run = 6 + 3 * big;
  if (u < 0.7) return 24 - 20 * Math.pow(u / 0.7, 1.3);
  if (u < 0.84) return 4 - run * smooth(0.7, 0.84, u);
  return 4 - run + 0.45 * run * smooth(0.84, 1, u);
}
function drawWaves(t) {
  const n0 = Math.floor(t / WAVE_P), sp = step(t, 1440), slow = step(t, 360);
  for (let j = 2; j >= 0; j--) {
    const n = n0 - j, u = (t - n * WAVE_P) / WAVE_LIFE;
    if (u >= 1) continue;
    const id = ((n % 30) + 30) % 30, big = SET_AMP[id % 6] * (0.85 + 0.15 * hash(id, 81));
    let rx = 0, ry = 0, rk = -1, ru = 0;
    const flush = xe => {                             // draw one run of columns sharing a row and a phase
      if (rk < 0) return;
      const w = xe - rx;
      if (rk === 0) {                                 // swell: a soft light line over its shadow
        const a = smooth(0, 0.3, ru) * (0.3 + 0.45 * big);
        ctx.globalAlpha = a; rect(ctx, rx, ry, w, 1, '#5a9ea8');
        ctx.globalAlpha = a * 0.8; rect(ctx, rx, ry + 1, w, 1, '#07182a');
      } else if (rk === 1) {                          // steepening crest with first white caps
        const br = smooth(0.45, 0.66, ru);
        ctx.globalAlpha = 0.55 + 0.35 * big; rect(ctx, rx, ry, w, 1, br > 0.6 ? '#d4f0ec' : '#8cc4c8');
        ctx.globalAlpha = 0.7; rect(ctx, rx, ry + 1, w, 1, '#0a1e2e');
        ctx.fillStyle = '#ffffff';
        for (let x = rx; x < xe; x++) {
          const hv = hash(x, id, sp);
          if (hv < br * big * 0.5) { ctx.globalAlpha = 0.9; ctx.fillRect(x, ry - 1, 1, 1); }
          else if (hash(x, id, 84) < 0.3 * br) { ctx.globalAlpha = 0.5; ctx.fillRect(x, ry - 1, 1, 1); }
        }
      } else if (rk === 2) {                          // breaking: bright foam, churning spray, whitewater behind
        const th = big > 0.6 ? 2 : 1;
        ctx.globalAlpha = 0.95; rect(ctx, rx, ry, w, 1, '#f4fffc');
        if (th > 1) { ctx.globalAlpha = 0.85; rect(ctx, rx, ry + 1, w, 1, '#d8eeea'); ctx.globalAlpha = 0.45; rect(ctx, rx, ry + 2, w, 1, '#9ccac8'); }
        ctx.globalAlpha = 0.55; rect(ctx, rx, ry + th + (th > 1 ? 1 : 0), w, 1, '#0b2232');
        ctx.fillStyle = '#e6f8f4';
        for (let x = rx; x < xe; x++) if (hash(x, id, 83) < 0.55) { ctx.globalAlpha = 0.75; ctx.fillRect(x, ry - 1, 1, 1); }
        if (big > 0.5) {
          const ly = ry - 3 - Math.floor((ru - 0.66) * 20);
          ctx.fillStyle = '#a8d6d4';
          for (let x = rx; x < xe; x++) if (hash(x >> 1, id, 85) < 0.6) { ctx.globalAlpha = 0.4; ctx.fillRect(x, ly, 1, 1); }
        }
        ctx.fillStyle = '#b8e0dc';
        for (let x = rx; x < xe; x++) {
          const hv = hash(x, id, sp);
          if (hv < 0.5 * big + 0.15) { ctx.globalAlpha = 0.45 + 0.45 * hv; ctx.fillRect(x, ry - 1 - Math.floor(hv * 5 * big), 1, 1); }
          const hw = hash(x + 7, id, slow);
          if (hw < 0.28 * big) { ctx.globalAlpha = 0.35 + hw; ctx.fillRect(x, ry - 3 - Math.floor(hw * 30), 1, 1); }
        }
      } else {                                        // run-up sheet on the sand, lace edge, backwash
        const f = 1 - smooth(0.84, 1, ru), sy = Math.round(shoreF(rx)), top = Math.min(ry, sy), hh = Math.abs(ry - sy) + 1;
        ctx.globalAlpha = 0.42 * f; rect(ctx, rx, top, w, hh, '#8fb8bc');
        ctx.globalAlpha = 0.9 * f; rect(ctx, rx, ry, w, 1, '#eefcf8');
        ctx.fillStyle = '#5a8a92';
        for (let x = rx; x < xe; x++) if (hash(x, id, 82) < 0.22) { ctx.globalAlpha = 0.8 * f; ctx.fillRect(x, ry, 1, 1); }
        ctx.fillStyle = '#d8f0ec';
        for (let x = rx; x < xe; x++) { const hv = hash(x, id, slow); if (hv < 0.2 * f) { ctx.globalAlpha = 0.6; ctx.fillRect(x, ry - 2 - Math.floor(hv * 25), 1, 1); } }
      }
    };
    for (let x = 0; x <= PIL; x++) {
      let k = -1, y = 0, ux = 0;
      if (x < PIL) {
        ux = u + 0.035 * Math.sin(x * 0.011 + id * 1.3) + 0.02 * Math.sin(x * 0.037 + id);
        const gap = Math.sin(x * 0.083 + id * 1.7) + 0.6 * Math.sin(x * 0.21 + id * 0.7);
        if (ux >= 0 && ux < 1 && gap >= -1.5 + (1 - big) * 1.3 - (ux > 0.62 ? 0.5 : 0)) {
          k = ux < 0.45 ? 0 : ux < 0.66 ? 1 : ux < 0.84 ? 2 : 3;
          y = Math.round(shoreF(x) - waveDist(ux, big));
        }
      }
      if (k !== rk || y !== ry) { flush(x); rx = x; ry = y; rk = k; ru = ux; }
    }
  }
  ctx.globalAlpha = 1;
}

/* ================= Beach life: lights, palms, people, traffic ================= */
function drawBeachLights(t) {
  for (const k of KIOSKS) {
    const base = promY(k.x) - 1;
    halo(HL.kiosk, k.x, base - 5, 0.9 + 0.1 * wave(t, 700 + k.x));
    halo(HL.ksign[KIOSKS.indexOf(k)], k.x, base - K_SIGN, 0.8 + 0.2 * wave(t, 900 + k.x));
  }
  for (const b of KBULBS) {
    const I = 0.6 + 0.4 * wave(t, b.k, b.ph);
    ctx.globalAlpha = 0.55 + 0.45 * I; px(ctx, b.x, b.y, b.col);
    halo(HL.kbulb, b.x, b.y, I * 0.9);
  }
  ctx.globalAlpha = 1;
  for (const lx of LAMPS) {
    const ly = promY(lx) + PROM_H - 17;
    halo(HL.lamp, lx, ly, 0.9 + 0.1 * wave(t, 1100 + lx));
    halo(HL.lampCore, lx, ly, 1);
  }
}
function drawPalms(t) {
  for (const p of PALMS) {
    const a = 0.75 * wave(t, p.k, p.ph) + 0.25 * wave(t, p.k2, p.ph * 2);
    const f = Math.round((a + 1) / 2 * (PALM_NF - 1));
    ctx.drawImage(PALM_C[p.i][f], Math.round(p.x + p.lean) - PALM_OX + 1, p.base - p.h - PALM_OY);
  }
}
// People on the beach are 7 px tall with a 2 x 2 head, so they read as people at this distance. x is their left column,
// feet on row y. view 0: from behind, 1: facing us (4 px wide, arms either side of a 2 px body; kick: one leg out to the
// side); 2: in profile, 2 px wide (dir 1 right, -1 left; fr: the stride frame, when the near arm swings forward).
const SKIN_B = ['#e0b088', '#c08a68', '#9a6a4c', '#6e4834'], HAIR_B = ['#1a1214', '#3a2418', '#6a4422', '#c8a060', '#c8c4c0'];
const beachLook = (skin, hair, top, bot, long = 0) => ({ skin: SKIN_B[skin], hair: HAIR_B[hair], top: top || SKIN_B[skin], bot, long });
const fill = (x, y, w, h, col) => { ctx.fillStyle = col; ctx.fillRect(x, y, w, h); };
function beachPerson(x, y, P, view, dir, fr, kick) {
  if (view === 2) {
    fill(x, y - 4, 2, 2, P.top); fill(x, y - 2, 2, 1, P.bot);
    if (fr) { fill(x, y - 1, 2, 1, P.skin); fill(x - 1, y, 1, 1, P.skin); fill(x + 2, y, 1, 1, P.skin); fill(dir > 0 ? x + 2 : x - 1, y - 3, 1, 1, P.skin); }
    else fill(x, y - 1, 2, 2, P.skin);
    fill(x, y - 6, 2, 1, P.hair); fill(dir > 0 ? x + 1 : x, y - 5, 1, 1, P.skin); fill(dir > 0 ? x : x + 1, y - 5, 1, 1 + P.long, P.hair);
    return;
  }
  fill(x, y - 4, 4, 1, P.top); fill(x + 1, y - 3, 2, 1, P.top); fill(x, y - 3, 1, 1, P.skin); fill(x + 3, y - 3, 1, 1, P.skin);
  fill(x + 1, y - 2, 2, 1, P.bot); fill(x + 1, y - 1, 1, 2, P.skin);
  if (kick) { fill(x + 2, y - 1, 1, 1, P.skin); fill(x + 3, y - 2, 2, 1, P.skin); } else fill(x + 2, y - 1, 1, 2, P.skin);
  fill(x + 1, y - 6, 2, 1, P.hair); fill(x + 1, y - 5, 2, 1, view ? P.skin : P.hair);
  if (P.long && !view) fill(x + 1, y - 4, 2, P.long, P.hair);
}
const WALKERS = [                                     // strollers, a jogger and a dog walker on the calçadão
  { n: 4, ph: 0.1, dir: 1, dy: 3, P: beachLook(1, 0, '#d8584a', '#2a3450') },
  { n: 5, ph: 0.55, dir: -1, dy: 5, P: beachLook(0, 3, '#e8dcc0', '#4a5a7a', 2) },
  { n: 3, ph: 0.3, dir: 1, dy: 6, dog: true, P: beachLook(2, 1, '#4a86c8', '#2a2a3c') },
  { n: 7, ph: 0.8, dir: -1, dy: 4, jog: true, P: beachLook(3, 0, '#f0c040', '#1e2a3a') },
  { n: 4, ph: 0.68, dir: -1, dy: 3, P: beachLook(1, 1, '#8a52b8', '#2a2a3c'), P2: beachLook(0, 2, '#e07ab0', '#e07ab0', 2) },
  { n: 6, ph: 0.42, dir: 1, dy: 5, P: beachLook(2, 4, '#3aa888', '#3a3040') },
];
function drawWalkers(t) {
  for (const w of WALKERS) {
    const { u } = cycle(t, w.n, w.ph);
    const x = Math.round(w.dir > 0 ? lerp(-14, W + 14, u) : lerp(W + 14, -14, u));
    if (x < -8 || x > W + 8) continue;
    const y = promY(clamp(x, 0, W - 1)) + w.dy;
    const fr = Math.floor(t * (w.jog ? 6 : 4)) & 1;
    beachPerson(x, y - (w.jog && fr ? 1 : 0), w.P, 2, w.dir, fr);
    if (w.P2) beachPerson(x - 4 * w.dir, y + 1, w.P2, 2, w.dir, fr ^ 1);
    if (w.dog) {
      const dx = x - 6 * w.dir;
      rect(ctx, dx, y - 2, 3, 1, '#7a5638'); px(ctx, dx + (w.dir > 0 ? 3 : -1), y - 3, '#7a5638');
      px(ctx, dx + (fr ? 0 : 1), y - 1, '#5a3e2a'); px(ctx, dx + (fr ? 2 : 1), y - 1, '#5a3e2a');
    }
  }
}
// A futevôlei game at the volleyball net under the lamp: a rally every 6 s (40 a loop). The ball is kicked over the net,
// touched up to a teammate and sent back. The far pair (facing us) stands behind the net and is drawn before it.
const FUT_N = 40, FUT_NET = promY(262) - 5;          // the net's foot
const FUT_P = [
  { x: 256, dy: -3, view: 1, P: beachLook(2, 0, null, '#2a4a8a') }, { x: 264, dy: -4, view: 1, P: beachLook(1, 1, null, '#2a4a8a') },
  { x: 257, dy: 3, view: 0, P: beachLook(1, 3, '#e8c040', '#1e2a3a') }, { x: 265, dy: 4, view: 0, P: beachLook(3, 0, '#e8e4dc', '#1e2a3a') },
];
const FUT_HITS = [[0, 2, 1, 16], [0.3, 1, 0, 6], [0.45, 0, 3, 16], [0.75, 3, 2, 5], [0.9, 2, 2, 4]];   // [u, from, to, arc height]
function drawFutevolei(t, near) {
  const { u } = cycle(t, FUT_N);
  for (let p = 0; p < 4; p++) {
    const f = FUT_P[p];
    if ((f.view === 0) !== near) continue;
    const kick = FUT_HITS.some(([h0, from]) => from === p && frac(u - h0 + 0.02) < 0.06);
    beachPerson(f.x, FUT_NET + f.dy, f.P, f.view, 1, 0, kick);
  }
  let i = FUT_HITS.length - 1;
  while (FUT_HITS[i][0] > u) i--;
  const [h0, a, b, arc] = FUT_HITS[i], h1 = i + 1 < FUT_HITS.length ? FUT_HITS[i + 1][0] : 1, v = (u - h0) / (h1 - h0);
  const A = FUT_P[a], B = FUT_P[b];
  if ((lerp(A.view, B.view, v) < 0.5) !== near) return;                   // the ball's side of the net
  px(ctx, Math.round(lerp(A.x, B.x, v) + 1.5), Math.round(lerp(FUT_NET + A.dy, FUT_NET + B.dy, v) - 4 - arc * 4 * v * (1 - v)), '#f4ecd8');
}
// a couple walking hand in hand along the waterline, once a loop
const STROLL = { ph: 0.62, P: [beachLook(1, 1, '#d86a8a', '#d86a8a', 2), beachLook(2, 0, '#e8e4dc', '#4a5a7a')] };
function drawStroll(t) {
  const { u } = cycle(t, 1, STROLL.ph);
  if (u >= 0.5) return;
  const x = Math.round(lerp(W + 10, -12, u * 2)), fr = Math.floor(t * 3.2) & 1;
  STROLL.P.forEach((P, k) => beachPerson(x + 4 * k, shoreY(clamp(x + 4 * k, 0, W - 1)) + 4, P, 2, -1, fr ^ k));
  rect(ctx, x + 2, shoreY(clamp(x, 0, W - 1)) + 1, 2, 1, STROLL.P[0].skin);              // their hands
}
const LANES = [{ off: 3, dir: 1, big: 0 }, { off: 9, dir: 1, big: 0 }, { off: 17, dir: -1, big: 1 }, { off: 24, dir: -1, big: 1 }];
const RIPPLES = [];                                  // moonlit wavelets glinting across the bay
{ const r = rng(515); for (let i = 0; i < 46; i++) RIPPLES.push({ id: i, x: Math.floor(Math.pow(r(), 0.7) * 420) + 20, y: HZ + 2 + Math.floor(r() * 28), w: 2 + Math.floor(r() * 4), n: 180 + Math.floor(r() * 120), ph: r() }); }
function drawRipples(t) {
  ctx.fillStyle = '#7cc2c4';
  for (const r of RIPPLES) {
    const { cyc, u } = cycle(t, r.n, r.ph);
    if (hash(r.id, cyc, 58) < 0.45) continue;
    const near = 1 - Math.min(1, Math.abs(r.x - MOON.x) / 160);
    ctx.globalAlpha = Math.sin(Math.PI * u) * (0.18 + 0.4 * near);
    ctx.fillRect(r.x + Math.round((hash(r.id, cyc, 59) - 0.5) * 30), r.y, r.w, 1);
  }
  ctx.globalAlpha = 1;
}
const CARS = [];
{
  const r = rng(90);
  for (let i = 0; i < 14; i++) CARS.push({ id: i, lane: i % 4, n: 20 + Math.floor(r() * 14), ph: r(),
    col: ['#6a6a78', '#7a2a2e', '#243e70', '#8a8478', '#2a2a34'][Math.floor(r() * 5)] });
}
function drawCars(t) {
  for (const c of CARS) {
    const { cyc, u } = cycle(t, c.n, c.ph);
    if (hash(c.id, cyc, 91) < 0.4) continue;
    const L = LANES[c.lane], len = L.big ? 11 : 8, hgt = L.big ? 4 : 3;
    const x = Math.round(L.dir > 0 ? lerp(-40, W + 40, u) : lerp(W + 40, -40, u));
    if (x < -30 || x > W + 30) continue;
    const y = promY(clamp(x, 0, W - 1)) + PROM_H + L.off, x0 = x - (len >> 1);
    rect(ctx, x0, y - hgt + 1, len, hgt, c.col);
    rect(ctx, x0 + 2, y - hgt + 1, len - 4, 1, '#12121a');
    const fx = L.dir > 0 ? x0 + len - 1 : x0, bx = L.dir > 0 ? x0 : x0 + len - 1;
    px(ctx, fx, y, '#fff6d8'); px(ctx, bx, y, '#ff3a3a');
    halo(HL.head, fx + L.dir * (L.big ? 8 : 6), y, 0.75);
    halo(HL.tail, bx, y, 0.6);
  }
}

/* ================= Our balcony: cobogó parapet, pillar with a sconce, hammock post, caipirinha ================= */
const CELL = 9, COB_Y = LEDGE + 4, COB_ROWS = 3;      // cobogó: perforated concrete blocks, the street shows through
const SCONCE = { x: 471, y: 118 };
const FAIRY = [], FWIRE = [];
{
  const r = rng(404), pinY = LEDGE + 3;
  for (let x0 = -6; x0 < PIL; x0 += 34) {
    const x1 = x0 + 34;
    for (let i = x0; i < x1; i++) {
      if (i < 0 || i >= PIL) continue;
      const u = (i - x0) / (x1 - x0), y = Math.round(pinY + 24 * u * (1 - u));
      FWIRE.push([i, y]);
      if ((i - x0) % 6 === 3) {
        const c = r();
        FAIRY.push({ id: FAIRY.length, x: i, y: y + 1, k: 17 + Math.floor(r() * 50), ph: r(),
          ci: c < 0.45 ? 0 : c < 0.7 ? 1 : c < 0.82 ? 2 : c < 0.92 ? 3 : 4 });
      }
    }
  }
  for (let j = 0; j < 5; j++) {                       // a string spiralling down the hammock post
    const y = 212 + j * 10, sx = j & 1 ? POST + 2 : POST - 2;
    FAIRY.push({ id: FAIRY.length, x: sx, y, k: 23 + j * 9, ph: r(), ci: j % 2 });
  }
}
const FAIRY_COLS = ['#fff0c4', '#ffc864', '#ff8ab8', '#7affe0', '#ffa050'];
const GLASS = { x: 268, y: 218 };
const GLASS_ROWS = ['......s.', '.....s..', 'wllwwsww', 'wllq.s.w', 'wqiqsqiw', 'wqqsiqqw', 'wiqsqqiw', 'wcLcccLw', 'wcccLccw', '.wwwwww.'];
const GLASS_C = { w: '#b8d8e2', l: '#7ae04a', q: '#e2eeb4', i: '#ffffff', s: '#ff5a5a', c: '#9ad04c', L: '#3a9a2a' };
// her little radio on the cap between the glass and the hammock post: handle, grille, a warm-lit dial, aerial
const RADIO = { x: 281, y: LEDGE - 8 };
const RADIO_ROWS = ['...hhhhhhh...', '...h.....h...', 'cCCCCCCCCCCCc', 'cGgGgGgFDDDFc', 'cgGgGgGFDnDFc', 'cGgGgGgFFFFFc', 'cgGgGgGCoCoCc',
  'bbbbbbbbbbbbb'];
const RADIO_C = { h: '#3a2a24', c: '#24645e', C: '#4aa89c', G: '#183836', g: '#7a9a92', F: '#14201e', D: '#ffcf7a', n: '#d0402a',
  o: '#c8c8d0', b: '#123432' };
const RADIO_DIAL = { x: RADIO.x + 9, y: RADIO.y + 4 };
function renderBalcony() {
  const [c, x] = makeCanvas(W, H);
  for (let i = 0; i < PIL; i++) {                     // parapet cap, warm-lit top face
    px(x, i, LEDGE, hash(i, 1, 101) < 0.1 ? '#e2d2cc' : '#c8b6b4');
    px(x, i, LEDGE + 1, '#a08e9a');
    px(x, i, LEDGE + 2, bayer(i, LEDGE + 2) < 0.5 ? '#766678' : '#6e5e72');
    px(x, i, LEDGE + 3, '#34283e');
  }
  for (let y = COB_Y; y < COB_Y + CELL * COB_ROWS; y++) for (let i = 0; i < PIL; i++) {
    const bx = (i + 3) % CELL, by = (y - COB_Y) % CELL;
    let col;
    if (bx === CELL - 1 || by === CELL - 1) col = '#3c3246';                  // mortar joint
    else {
      const d = Math.hypot(bx - 3.5, by - 3.5);
      if (d < 2.6) continue;                                                 // round hole
      if (d < 3.6) col = by < 3.5 ? (bx < 3.5 ? '#2e2638' : '#382e42') : (bx > 3.5 ? '#8a7886' : '#766876');
      else col = (bx * 3 + by * 5) % 11 === 0 ? '#645666' : '#5a4e5e';
    }
    px(x, i, y, col);
  }
  const BY = COB_Y + CELL * COB_ROWS;
  for (let y = BY; y < H; y++) for (let i = 0; i < PIL; i++)
    px(x, i, y, y === BY ? '#76687a' : y === BY + 1 ? '#46384e' : bayer(i, y) < (y - BY) / 12 ? '#1e1828' : '#32283c');
  for (const [i, y] of FWIRE) px(x, i, y, '#2a1e1e');
  // right pillar: moonlit edge, plaster, a warm pool of light around the sconce
  for (let y = 0; y < H; y++) for (let i = PIL; i < W; i++) {
    const fl = i - PIL, pool = 1 - Math.hypot((i - SCONCE.x) / 20, (y - SCONCE.y - 6) / 40);
    let col = fl === 0 ? '#5a6484' : fl === 1 ? '#3e4462' : hash(i, y >> 1, 102) < 0.08 ? '#34304a' : '#2c2a42';
    if (pool > 0.05 + bayer(i, y) * 0.5) col = fl === 0 ? '#8a7078' : pool > 0.62 ? '#665058' : pool > 0.3 ? '#4e3e50' : '#3c3248';
    px(x, i, y, col);
  }
  rect(x, SCONCE.x - 3, SCONCE.y - 5, 7, 2, '#3a2a1e'); rect(x, SCONCE.x - 2, SCONCE.y - 3, 5, 6, '#ffcf7a');
  rect(x, SCONCE.x - 1, SCONCE.y - 2, 3, 3, '#fff4d0'); rect(x, SCONCE.x - 3, SCONCE.y + 3, 7, 1, '#3a2a1e'); px(x, SCONCE.x, SCONCE.y + 4, '#2a1e16');
  rect(x, PIL - 2, HOOK_R.y - 1, 2, 3, '#8a8698'); px(x, PIL - 2, HOOK_R.y, '#d8d4e0');
  // hammock post
  rect(x, POST - 1, 206, 3, H - 206, '#3a2418'); rect(x, POST - 1, 206, 1, H - 206, '#6e4430'); rect(x, POST + 1, 206, 1, H - 206, '#26160e');
  for (let y = 210; y < H; y += 7) px(x, POST, y, '#4a2e1e');
  rect(x, POST - 2, 203, 5, 3, '#4a2e1e'); rect(x, POST - 2, 203, 5, 1, '#8a5a3a');
  px(x, POST + 2, HOOK_L.y, '#d8d4e0'); px(x, POST + 2, HOOK_L.y + 1, '#8a8698');
  // caipirinha on the cap: crushed ice, lime, a straw
  GLASS_ROWS.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (GLASS_C[row[i]]) px(x, GLASS.x + i, GLASS.y + j, GLASS_C[row[i]]); });
  px(x, GLASS.x + 1, GLASS.y + 4, '#f4fff0'); px(x, GLASS.x + 1, GLASS.y + 5, '#dff0e0');
  rect(x, GLASS.x - 1, LEDGE, 10, 1, '#b8a8a8');
  RADIO_ROWS.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (RADIO_C[row[i]]) px(x, RADIO.x + i, RADIO.y + j, RADIO_C[row[i]]); });
  for (let k = 1; k <= 9; k++) px(x, RADIO.x + 11 + Math.round(k * 0.45), RADIO.y + 2 - k, k === 9 ? '#e8e8f4' : '#9a9aac');   // aerial
  rect(x, RADIO.x - 1, LEDGE, 15, 1, '#b8a8a8');
  return c;
}
function drawFairyLights(t) {
  for (const b of FAIRY) {
    const I = (0.55 + 0.45 * wave(t, b.k, b.ph)) * (0.72 + 0.28 * wave(t, 12, -b.x / 150));
    halo(HL.fairy[b.ci], b.x, b.y + 1, I);
    ctx.globalAlpha = 0.5 + 0.5 * I;
    px(ctx, b.x, b.y, FAIRY_COLS[b.ci]); px(ctx, b.x, b.y + 1, FAIRY_COLS[b.ci]);
    ctx.globalAlpha = I; px(ctx, b.x, b.y, '#ffffff');
  }
  ctx.globalAlpha = 1;
  const [gx, gy0] = geckoPos(t);                      // the gecko on the pillar (behind the leaves)
  GECKO.forEach((row, j) => { for (let i = 0; i < row.length; i++) {
    const ch = row[i];
    if (ch === '.') continue;
    const wag = ch === 't' && j >= 8 ? Math.round(wave(t, 36)) : 0;
    px(ctx, gx - 2 + i + wag, gy0 - 4 + j, GECKO_C[ch]);
  } });
  const g = frac(t / 6);                              // a glint slides down the caipirinha glass every 6 s
  if (g < 0.1) { const gy = GLASS.y + 3 + Math.floor(g / 0.02); px(ctx, GLASS.x + 6, gy, '#ffffff'); halo(HL.glint, GLASS.x + 6, gy, 1 - g / 0.1); }
  halo(HL.radio, RADIO_DIAL.x, RADIO_DIAL.y, 0.85 + 0.15 * wave(t, 160));   // the radio's dial
}

/* ================= A house gecko hunting moths around the sconce ================= */
const GECKO = ['..h..', '.ehe.', '..b..', 'lbbbl', '.bbb.', 'lbbbl', '..t..', '..t..', '...t.', '...t.'];
const GECKO_C = { h: '#d4c4a4', e: '#1a1410', b: '#bcac8c', l: '#8a7a62', t: '#a89878' };
const GECKO_STOPS = [[0, 469, 138], [44, 469, 138], [44.7, 470, 129], [108, 470, 129], [108.7, 469, 138], [166, 469, 138],
  [166.7, 474, 146], [228, 474, 146], [228.7, 469, 138], [240, 469, 138]];
function geckoPos(t) {
  for (let i = 0; i < GECKO_STOPS.length - 1; i++) {
    const [t0, x0, y0] = GECKO_STOPS[i], [t1, x1, y1] = GECKO_STOPS[i + 1];
    if (t >= t0 && t < t1) { const u = smooth(t0, t1, t); return [Math.round(lerp(x0, x1, u)), Math.round(lerp(y0, y1, u))]; }
  }
  return [469, 138];
}
const MOTHS = [{ k: 70, ph: 0.1, rx: 9, ry: 6 }, { k: 83, ph: 0.55, rx: 12, ry: 8 }, { k: 61, ph: 0.8, rx: 7, ry: 10 }];
function drawMoths(t) {
  const flap = step(t, 2400) & 1;
  MOTHS.forEach((m, i) => {
    const a = TAU * (m.k * t / LOOP + m.ph);
    const x = SCONCE.x - 3 + Math.cos(a) * m.rx + Math.sin(a * 3 + i) * 1.5, y = SCONCE.y + Math.sin(a) * m.ry + Math.cos(a * 2 + i) * 1.2;
    halo(HL.moth, x, y, 0.8);
    px(ctx, Math.round(x), Math.round(y), '#fff2dc');
    if (flap) px(ctx, Math.round(x) + 1, Math.round(y), '#d8cab0');
  });
}

/* ================= Hammock (pre-rendered swing frames) and the woman sitting in it ================= */
const HAM_NF = 24, HAM_X0 = POST - 2, HAM_Y0 = 204, HAM_W = PIL - POST + 4, HAM_H = 60;
const HAM_OUT = ['#c04a3c', '#dca444', '#3c8c64', '#3e62a4'], HAM_IN = ['#662a2c', '#745628', '#1e4838', '#22386a'];
// She sits across the hammock with her back to us, her hair up in a bun, looking out over the bay, and sways with it.
// Sprite rows 0-27 from the top of her bun; row 23 is the row she sits on (the hammock's near side hides what is below).
const SIT = { x: 378, sip: 10, turn: 6 };            // sip: sips of her caipirinha per loop; turn: looks round at the statue
const SIT_HEAD = [                                    // rows 0-7: bun, scrunchie, head, ears, nape; then turned towards the statue
  ['.......hh.......', '......hHHw......', '.......tt.......', '......hHHw......', '.....hHHHHw.....', '....sHHHHHwk....',
    '.....HHHHHw.....', '......sSSk......'],
  ['......hh........', '.....hHHw.......', '......tt........', '.....hHHw.......', '....sHHHHw......', '...ssHHHHw......',
    '....sHHHHw......', '......sSSk......'],
];
const SIT_BODY = [                                    // rows 8-27: neck, bare shoulders, a printed sundress, her left arm
  '......sSSk......', '...sSdSSSSdSk...', '..sSSdDDDDdS....', '..sSdDDfDDDe....', '..sSdDDDDDDe....', '..sSdfDDDfDe....',
  '..sSdDDDDDDe....', '..sSdDDfDDDe....', '..sSdDDDDDDe....', '...sdfDDDfDe....', '....dDDDDDDe....', '...ddDDfDDDee...',
  '...dDDDDDDDDe...', '...dDfDDDfDDe...', '...dDDDDDDDDe...', '...dDDDfDDDDe...', '...dDDDDDDDDe...', '...dDDDDDDDDe...',
  '...dDDDDDDDDe...', '...dDDDDDDDDe...',
];
// her right arm: resting with the glass on the hammock's edge, lifting it, sipping (the glass tips up beside her head)
const SIT_ARM = [
  ['...........Sk', '............Sk', '............Sk', '............Sk', '............Sk', '............Sk', '............Skgg',
    '............SkLi', '............sSLL', '.............Skk'],
  ['...........Sk', '............Sk', '............Skgg', '............SkLi', '............SkLL', '............Skkk', '............sSS',
    '.............s'],
  ['...........gL', '...........kL', '...........kS', '............Sk', '...........SSk', '...........SSk', '............SSk',
    '.............Sk', '.............sk'],
];
const SIT_ARM_Y = [9, 9, 4];                          // the sprite row of each pose's first string
const SIT_C = { H: '#34201e', h: '#7a7494', w: '#8a5a3c', S: '#a8714e', s: '#7a4e38', k: '#d49a6a',
  D: '#c45a4c', d: '#8a3a36', e: '#e88a6c', f: '#eeb49a', t: '#3ab0a0', g: '#d8eef0', L: '#9ad04c', i: '#f0f8e0' };
function renderSitter(rows, y0) {                     // one layer of her sprite (18 x 28)
  const [c, x] = makeCanvas(18, 28);
  rows.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (SIT_C[row[i]]) px(x, i, y0 + j, SIT_C[row[i]]); });
  return c;
}
function drawSitter(t, seat) {
  const x0 = SIT.x - 8, y0 = seat - 23;
  const { u } = cycle(t, SIT.sip, 0.3), arm = u < 0.6 || u >= 0.8 ? 0 : u < 0.64 || u >= 0.76 ? 1 : 2;
  const look = cycle(t, SIT.turn, 0.1).u;
  ctx.drawImage(SIT_BC, x0, y0);
  ctx.drawImage(SIT_HC[look >= 0.2 && look < 0.55 ? 1 : 0], x0, y0);
  ctx.drawImage(SIT_AC[arm], x0, y0);
}
function renderHammock(ph) {                          // [the hammock behind its sitter, the near side in front of her]
  const [c, x] = makeCanvas(HAM_W, HAM_H), [cf, xf] = makeCanvas(HAM_W, HAM_H);
  const put = (cx, gx, gy, col) => { const lx = gx - HAM_X0, ly = gy - HAM_Y0; if (lx >= 0 && ly >= 0 && lx < HAM_W && ly < HAM_H) px(cx, lx, ly, col); };
  const P = (gx, gy, col) => put(x, gx, gy, col), F = (gx, gy, col) => put(xf, gx, gy, col);
  const s = Math.sin(TAU * ph), lag = Math.sin(TAU * ph - 1.1);
  const EL = { x: 324, y: 220 + s * 0.7 }, ER = { x: 440, y: 219 + s * 0.7 };
  const rope = (hx, hy, ex, ey) => {                  // three cords from the hook to the gathered end
    for (const k of [-2.5, 0, 2.5]) {
      const ty = ey + k, steps = Math.ceil(Math.abs(ex - hx)) + 1;
      for (let j = 0; j <= steps; j++) {
        const u = j / steps;
        P(Math.round(lerp(hx, ex, u)), Math.round(lerp(hy, ty, u) + 0.8 * Math.sin(Math.PI * u)), k ? '#b09c7c' : '#dccba6');
      }
    }
  };
  rope(HOOK_L.x + 1, HOOK_L.y, EL.x, EL.y); rope(HOOK_R.x - 1, HOOK_R.y, ER.x, ER.y);
  const yb = [];
  let seat = 0;
  for (let gx = EL.x; gx <= ER.x; gx++) {
    const sp = (gx - EL.x) / (ER.x - EL.x), bell = Math.sin(Math.PI * sp), base = lerp(EL.y, ER.y, sp);
    const fB = base - 1 + (11 - 1.2 * s) * bell, fF = base + 1 + (19 + 1.8 * s) * bell, yB = Math.round(fB), yF = Math.round(fF);
    const yU = Math.round(yF + (6 + 0.8 * s) * Math.pow(bell, 0.8));
    yb[gx] = yB;
    if (gx === SIT.x) seat = Math.round(lerp(fB, fF, 0.75));                  // she sits low in the bowl
    for (let gy = yB; gy < yF; gy++) P(gx, gy, HAM_IN[Math.floor((gy - yB) / Math.max(1, yF - yB) * 4) % 4]);
    for (let gy = yF; gy <= yU; gy++) F(gx, gy, HAM_OUT[Math.floor((1 - (gy - yF) / Math.max(1, yU - yF + 1)) * 4) % 4]);
    P(gx, yB, '#dccdb2'); F(gx, yF, '#f2e6ca');
    if (sp > 0.05 && sp < 0.95) {                     // varanda: lace fringe with tassels swinging behind the motion
      if ((gx & 1) === 0) F(gx, yF + 1, '#eadec2');
      if (gx % 3 === 0) F(gx, yF + 2, '#d8ccb0');
      if (gx & 1) {
        const tl = 3 + (gx % 3 === 1 ? 1 : 0);
        for (let k = 2; k <= tl; k++) F(gx + (k >= 3 ? Math.round(lag * 0.8) : 0), yF + 1 + k, k === tl ? '#b8aa8e' : '#dccfb2');
      }
    }
  }
  for (const E of [EL, ER]) { for (let k = -3; k <= 3; k++) F(E.x, Math.round(E.y + k), '#8a7a60'); F(E.x, Math.round(E.y), '#c8b894'); }
  for (let gx = 419; gx <= 431; gx++) {               // a cushion resting at the right end
    const top = yb[gx] + 1, rim = gx === 419 || gx === 431;
    for (let k = rim ? 1 : 0; k < (rim ? 3 : 4); k++) P(gx, top + k, k === 0 ? '#f4ead6' : k === 3 ? '#a89a82' : '#e0d2ba');
  }
  return { back: c, front: cf, seat };
}

/* ================= Tropical plants (pre-rendered sway frames) ================= */
const PLANT_NF = 7, POT = { x: 438, y: 252 };
const MONSTERA = [
  { bx: 410, by: 188, dir: 196, L: 30, Wd: 26, k: 17, ph: 0.1, ax: -6 }, { bx: 432, by: 160, dir: 240, L: 32, Wd: 27, k: 13, ph: 0.4, ax: -2 },
  { bx: 456, by: 178, dir: 300, L: 27, Wd: 23, k: 19, ph: 0.7, ax: 4 }, { bx: 430, by: 218, dir: 160, L: 23, Wd: 20, k: 15, ph: 0.25, ax: -4 },
  { bx: 454, by: 216, dir: 14, L: 22, Wd: 19, k: 21, ph: 0.55, ax: 6 },
];
const STREL = [
  { kind: 'leaf', bx: 448, by: 150, dir: 258, L: 36, Wd: 11, k: 11, ph: 0.3, ax: 10 },
  { kind: 'leaf', bx: 456, by: 142, dir: 268, L: 30, Wd: 10, k: 9, ph: 0.6, ax: 14 },
  { kind: 'leaf', bx: 426, by: 154, dir: 226, L: 28, Wd: 10, k: 13, ph: 0.9, ax: 6 },
  { kind: 'flower', bx: 416, by: 170, k: 12, ph: 0.2, ax: 4 }, { kind: 'flower', bx: 444, by: 146, k: 10, ph: 0.75, ax: 12 },
];
const BIRD = ['........o.......', '......o.oo......', '.....oo.ooo.....', '....ooo.oooB....', '...oooOOOOBB....', '..oooOOOOBBB....',
  '.pppppppppppPP..', 'pppppppppppppPP.', '.rrrrrrrrrrrr...', '.........gggg...', '............g...'];
const BIRD_C = { o: '#ff8a2a', O: '#ffc45e', B: '#4a6cff', p: '#4e5e4a', P: '#9a7a9a', r: '#6a3452', g: '#2a5a38' };
const BIRD_AX = 12, BIRD_AY = 10;
function monsteraAt(u, v, L, Wd) {
  const c = L * 0.46, a = L * 0.56, q = (u - c) / a;
  if (q < -1 || q > 1) return 0;
  let hw = Wd / 2 * Math.sqrt(1 - q * q);
  if (q > 0) hw *= 1 - 0.3 * q * q;
  const av = Math.abs(v);
  if (av > hw) return 0;
  if (u < L * 0.12 && av < (L * 0.12 - u) * 0.9) return 0;                 // heart notch
  const vein = (u - av * 0.55) / (L * 0.15), fv = vein - Math.floor(vein);
  if (av > hw * 0.45 && u > L * 0.12 && fv < 0.3) return 0;                // slits from the margin
  if (av > hw * 0.15 && av < hw * 0.34 && fv > 0.42 && fv < 0.7 && u > L * 0.22 && u < L * 0.8) return 0;   // holes
  if (av < 0.75) return 3;
  return av > hw - 1.1 ? 1 : 2;
}
function strelAt(u, v, L, Wd) {
  if (u < 0 || u > L) return 0;
  const q = (u - L / 2) / (L / 2), av = Math.abs(v);
  const hw = Wd / 2 * Math.sqrt(Math.max(0, 1 - q * q)) * (q > 0 ? 1 - 0.25 * q : 1);
  if (av > hw) return 0;
  const w = (u + av * 0.9) / 4.2, fw = w - Math.floor(w);
  if (av > 1.2 && fw < 0.22 && hash(Math.floor(w), v > 0 ? 1 : 2, 7) < 0.45) return 0;   // wind tears
  if (av < 0.7) return 3;
  return av > hw - 1 ? 1 : 2;
}
function renderPot() {
  const [c, x] = makeCanvas(W, H);
  for (let y = 249; y < H; y++) {                    // terracotta pot, mostly below the frame
    const inset = Math.floor((y - 249) * 0.25);
    for (let i = 416 + inset; i <= 462 - inset; i++) {
      let col = y < 252 ? (y === 249 ? '#c07650' : '#8e4e34') : bayer(i, y) < 0.3 ? '#6e3a28' : '#643424';
      if (i > 454 - inset) col = y < 252 ? '#6a3626' : '#4a261c';
      else if (i < 421 + inset && y >= 252) col = '#7e4430';
      if (y === 252) col = '#3e1e16';
      px(x, i, y, col);
    }
  }
  return c;
}
function renderPlantUnit(u, isMon) {                  // stalk + blade (or flower), rigidly swaying about the pot
  const ax = POT.x + u.ax, ay = POT.y, amp = (isMon ? 1.6 : 1.9) * Math.PI / 180;
  const xs = [ax, u.bx], ys = [ay, u.by];
  if (u.kind === 'flower') { xs.push(u.bx - BIRD_AX - 1, u.bx + 5); ys.push(u.by - BIRD_AY - 1, u.by + 2); }
  else {
    const d = u.dir * Math.PI / 180, tx = u.bx + Math.cos(d) * u.L, ty = u.by + Math.sin(d) * u.L;
    xs.push(tx - u.Wd, tx + u.Wd, u.bx - u.Wd, u.bx + u.Wd); ys.push(ty - u.Wd, ty + u.Wd, u.by - u.Wd, u.by + u.Wd);
  }
  const M = 9, x0 = Math.floor(Math.min(...xs)) - M, y0 = Math.floor(Math.min(...ys)) - M;
  const w = Math.ceil(Math.max(...xs)) + M - x0, h = Math.ceil(Math.max(...ys)) + M - y0;
  const cx = lerp(ax, u.bx, 0.2), cy = lerp(ay, u.by, 0.8);
  const dir = (u.dir || 0) * Math.PI / 180, cd = Math.cos(dir), sd = Math.sin(dir);
  const C = isMon ? { eu: '#4a8a6a', el: '#0c281c', ew: '#8a8440', bu: '#27603c', bl: '#17442c', m: '#48884e', s: '#1e4a2a', sh: '#2e6038' }
    : { eu: '#4a8a76', el: '#10302a', ew: '#7a7a44', bu: '#2c6252', bl: '#1e4a40', m: '#7a8a60', s: '#28503e', sh: '#3a6a50' };
  const frames = [];
  for (let f = 0; f < PLANT_NF; f++) {
    const d = (f / (PLANT_NF - 1) * 2 - 1) * amp, cs = Math.cos(d), sn = Math.sin(d);
    const [cv, x] = makeCanvas(w, h);
    const img = x.createImageData(w, h), dat = img.data;
    const put = (i, j, col) => {
      if (i < 0 || j < 0 || i >= w || j >= h) return;
      const o = (j * w + i) * 4, rgb = HX(col); dat[o] = rgb[0]; dat[o + 1] = rgb[1]; dat[o + 2] = rgb[2]; dat[o + 3] = 255;
    };
    const len = Math.hypot(u.bx - ax, u.by - ay);
    for (let k = 0, n = Math.ceil(len * 2); k <= n; k++) {           // stalk (forward-rotated samples)
      const s = k / n, qx = (1 - s) * (1 - s) * ax + 2 * (1 - s) * s * cx + s * s * u.bx, qy = (1 - s) * (1 - s) * ay + 2 * (1 - s) * s * cy + s * s * u.by;
      const rx = ax + (qx - ax) * cs - (qy - ay) * sn, ry = ay + (qx - ax) * sn + (qy - ay) * cs;
      put(Math.floor(rx) - x0, Math.floor(ry) - y0, k & 2 ? C.sh : C.s);
    }
    for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) {         // blade / flower (inverse-mapped per pixel)
      const sx = x0 + i + 0.5, sy = y0 + j + 0.5;
      const qx = ax + (sx - ax) * cs + (sy - ay) * sn, qy = ay - (sx - ax) * sn + (sy - ay) * cs;
      if (u.kind === 'flower') {
        const is = Math.floor(qx - (u.bx - BIRD_AX)), js = Math.floor(qy - (u.by - BIRD_AY));
        if (js >= 0 && js < BIRD.length && is >= 0 && is < BIRD[0].length && BIRD_C[BIRD[js][is]]) put(i, j, BIRD_C[BIRD[js][is]]);
        continue;
      }
      const dx = qx - u.bx, dy = qy - u.by, lu = dx * cd + dy * sd, lv = -dx * sd + dy * cd;
      const k = isMon ? monsteraAt(lu, lv, u.L, u.Wd) : strelAt(lu, lv, u.L, u.Wd);
      if (!k) continue;
      const upper = lv * cd < 0, warm = Math.hypot(sx - SCONCE.x, sy - SCONCE.y) < 44;
      put(i, j, k === 3 ? C.m : k === 1 ? (upper ? C.eu : warm ? C.ew : C.el) : upper ? C.bu : C.bl);
    }
    x.putImageData(img, 0, 0);
    frames.push(cv);
  }
  return { u, frames, x0, y0 };
}
const FRONDS = [                                     // an areca palm arching up from the bottom-left corner
  { ax: 12, ay: 284, cx: 2, cy: 222, tx: 50, ty: 196, k: 11, ph: 0.15, len: 7 },
  { ax: 16, ay: 284, cx: 34, cy: 238, tx: 80, ty: 226, k: 14, ph: 0.5, len: 6 },
  { ax: 8, ay: 284, cx: -8, cy: 234, tx: 6, ty: 194, k: 9, ph: 0.8, len: 6 },
];
function renderFrond(f) {
  const amp = 2.2 * Math.PI / 180, M = 12;
  const x0 = Math.floor(Math.min(f.ax, f.cx, f.tx)) - M, y0 = Math.floor(Math.min(f.ay, f.cy, f.ty)) - M;
  const w = Math.ceil(Math.max(f.ax, f.cx, f.tx)) + M - x0, h = Math.min(H, Math.ceil(Math.max(f.ay, f.cy, f.ty)) + M) - y0;
  const frames = [];
  for (let fr = 0; fr < PLANT_NF; fr++) {
    const d = (fr / (PLANT_NF - 1) * 2 - 1) * amp, cs = Math.cos(d), sn = Math.sin(d);
    const [cv, x] = makeCanvas(w, h);
    const plot = (qx, qy, col) => {
      const rx = f.ax + (qx - f.ax) * cs - (qy - f.ay) * sn, ry = f.ay + (qx - f.ax) * sn + (qy - f.ay) * cs;
      const i = Math.floor(rx) - x0, j = Math.floor(ry) - y0;
      if (i >= 0 && j >= 0 && i < w && j < h) px(x, i, j, col);
    };
    for (let k = 0, N = 140; k <= N; k++) {
      const s = k / N, a = 1 - s;
      const qx = a * a * f.ax + 2 * a * s * f.cx + s * s * f.tx, qy = a * a * f.ay + 2 * a * s * f.cy + s * s * f.ty;
      const dx = 2 * a * (f.cx - f.ax) + 2 * s * (f.tx - f.cx), dy = 2 * a * (f.cy - f.ay) + 2 * s * (f.ty - f.cy);
      const dl = Math.hypot(dx, dy), ux = dx / dl, uy = dy / dl;
      if (s > 0.3 && k % 3 === 0) {                   // leaflets on both sides, swept forward and drooping
        const L = f.len * Math.pow(1 - (s - 0.3) / 0.7, 0.6) + 1.5;
        for (const side of [-1, 1]) {
          let lx = -uy * side * 0.7 + ux * 0.5, ly = ux * side * 0.7 + uy * 0.5 + 0.45;
          const ll = Math.hypot(lx, ly); lx /= ll; ly /= ll;
          const up = ly < -0.2;
          for (let m = 1; m <= L * 2; m++) {
            const e = m / 2;
            plot(qx + lx * e, qy + ly * e + e * e * 0.03, e > L - 1 ? (up ? '#4a8a62' : '#2e6040') : up ? '#2e6a46' : '#1c4630');
          }
        }
      }
      plot(qx, qy, '#3a6a3e');
    }
    frames.push(cv);
  }
  return { u: f, frames, x0, y0 };
}
function drawPlants(t) {
  ctx.drawImage(POT_C, 0, 0);
  for (const P of PLANTS) {
    const a = 0.7 * wave(t, P.u.k, P.u.ph) + 0.3 * wave(t, P.u.k * 3 + 1, P.u.ph * 1.7);
    ctx.drawImage(P.frames[Math.round((a + 1) / 2 * (PLANT_NF - 1))], P.x0, P.y0);
  }
}
function drawHammock(t) {                            // the hammock, her in it, then its near side in front of her
  const f = HAM_C[Math.floor(frac(t / 5) * HAM_NF) % HAM_NF];
  ctx.drawImage(f.back, HAM_X0, HAM_Y0);
  drawSitter(t, f.seat);
  ctx.drawImage(f.front, HAM_X0, HAM_Y0);
}

/* ================= Fireflies at three depths ================= */
const FLIES = [[], [], []];
{
  const r = rng(303);
  [[16, 0, 460, 158, 196, 18], [11, 0, 470, 186, 246, 26], [9, 300, 476, 128, 258, 26]].forEach(([n, xa, xb, ya, yb, amp], li) => {
    for (let i = 0; i < n; i++) FLIES[li].push({ bx: lerp(xa, xb, r()), by: lerp(ya, yb, r()), amp: amp * (0.6 + r() * 0.8),
      k1: 2 + Math.floor(r() * 6), k2: 9 + Math.floor(r() * 11), k3: 2 + Math.floor(r() * 6), k4: 8 + Math.floor(r() * 12),
      p1: r(), p2: r(), p3: r(), p4: r(), kb: 48 + Math.floor(r() * 50), pb: r() });
  });
}
function drawFlies(li, t) {
  for (const f of FLIES[li]) {
    const x = f.bx + f.amp * (0.7 * wave(t, f.k1, f.p1) + 0.3 * wave(t, f.k2, f.p2));
    const y = f.by + f.amp * 0.55 * (0.7 * wave(t, f.k3, f.p3) + 0.3 * wave(t, f.k4, f.p4));
    const b = Math.pow(Math.max(0, wave(t, f.kb, f.pb)), 3);
    halo(HL.fly[li], x, y, 0.12 + 0.88 * b);
    ctx.globalAlpha = 0.3 + 0.7 * b;
    rect(ctx, Math.round(x), Math.round(y), li === 2 ? 2 : 1, li === 2 ? 2 : 1, b > 0.4 ? '#f4ffb0' : '#a8d060');
  }
  ctx.globalAlpha = 1;
}

// a picked weather (site/js/engine/weather.js): Rio draws no falling weather of its own (the fireflies stay in every pick);
// far drops end at the far beach or on the bay, mid ones on the calçadão and the avenue, near ones fall past the
// balcony and out of the bottom, catching the kiosks and their signs, the street lamps, the sconce and the fairy lights
const WEATHER = weatherPlans([]);
const WX_LAND = [[HZ - 3, HZ + 12], [208, LEDGE - 2], [H + 4, H + 4]];
const WX_LIGHTS = [
  ...KIOSKS.map(k => ({ x: k.x, y: promY(k.x) - 1 - K_SIGN, rx: 12, ry: 6, col: k.col })),      // roof signs
  ...KIOSKS.map(k => ({ x: k.x, y: promY(k.x) - 7, rx: 24, ry: 13, col: '#ffc47a' })),
  ...LAMPS.map(lx => ({ x: lx, y: promY(lx) + PROM_H - 14, rx: 11, ry: 14 })),
  { x: SCONCE.x, y: SCONCE.y + 6, rx: 16, ry: 28 }, { x: RADIO_DIAL.x, y: RADIO_DIAL.y, rx: 5, ry: 5, col: '#ffcf7a' },
  ...FAIRY.map(b => ({ x: b.x, y: b.y + 1, rx: 6, ry: 6, col: FAIRY_COLS[b.ci] })),
];
const WX_LEAVES = { wind: [0, 16, W, 204] };          // the palms stand far below this balcony: the wind carries the leaves across the bay
const WX = { land: WX_LAND, lights: WX_LIGHTS, leaves: WX_LEAVES };
const WX_BB = { land: WX_LAND, leaves: WX_LEAVES, lights: [...WX_LIGHTS, { x: BB.x + (BB.w >> 1), y: BB.y + (BB.h >> 1), rx: 24, ry: 12 }] };   // + the floodlit face

function renderVignette() {
  const [c, x] = makeCanvas(W, H); const img = x.createImageData(W, H), d = img.data;
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.78, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q <= 0) continue;
    const o = (y * W + i) * 4; d[o] = 6; d[o + 1] = 4; d[o + 2] = 18; d[o + 3] = Math.round(q * 0.42 * 255);
  }
  x.putImageData(img, 0, 0);
  return c;
}

/* ================= Pre-render everything static ================= */
let HL, SKY_C, MOON_C, MTN_C, WATER_C, BOAT_C, BEACH, PALM_C, BAL_C, POT_C, HAM_C, SIT_BC, SIT_HC, SIT_AC, PLANTS, VIG_C, BBF_C, BB_AD, BB_GLINT, PITCH;
function prerender() {
  HL = {
    moon: makeHalo(62, '#6fd0c8', 0.2, 1.7), moonCore: makeHalo(22, '#e6fff4', 0.32, 1.5),
    christ: makeHalo(48, '#9cc4ff', 0.22, 1.7), christCore: makeHalo(16, '#eef4ff', 0.3, 1.4),
    sign: makeHalo(16, '#ff5a8a', 0.34), red: makeHalo(4, '#ff3030', 0.6, 1), station: makeHalo(7, '#ffd08a', 0.4),
    cabin: makeHalo(7, '#ffe0a0', 0.45), boat: makeHalo(9, '#ffd08a', 0.35), landing: makeHalo(9, '#ffffff', 0.5, 1.4),
    strobe: makeHalo(5, '#ffffff', 0.6, 1), flash: makeHalo(80, '#ffe6c8', 0.16, 1.5), flashW: makeHalo(40, '#ffe6c8', 0.14, 1.5),
    fwGlow: FW_COLS.map(p => makeHalo(40, p[1], 0.16, 1.6)),
    kiosk: makeHalo(28, '#ffb060', 0.28), ksign: KIOSKS.map(k => makeHalo(13, k.col, 0.26)), kbulb: makeHalo(3, '#ffd080', 0.5, 1),
    fwGlowW: FW_COLS.map(p => makeHalo(26, p[1], 0.14, 1.5)), moth: makeHalo(2, '#fff0c0', 0.45, 1),
    lamp: makeHalo(16, '#ffc070', 0.3), lampCore: makeHalo(5, '#fff0c8', 0.5, 1),
    head: makeHalo(7, '#fff0c0', 0.3), tail: makeHalo(3, '#ff3030', 0.45, 1),
    fairy: FAIRY_COLS.map(c => makeHalo(8, c, 0.5, 1.5)), glint: makeHalo(3, '#ffffff', 0.7, 1), radio: makeHalo(7, '#ffc070', 0.32, 1.3),
    sconce: makeHalo(44, '#ffb45a', 0.2, 1.7), sconceCore: makeHalo(9, '#ffe2a8', 0.45, 1.3),
    fly: [makeHalo(3, '#c8ff5a', 0.35, 1), makeHalo(6, '#c8ff5a', 0.32, 1.2), makeHalo(10, '#d8ff6a', 0.32, 1.3)],
    bbWash: makeHalo(24, '#ffc890', 0.07, 1.6), bbLamp: makeHalo(3, '#fff0c8', 0.18, 1.2),
  };
  BBF_C = renderBillboardFrame(); BB_AD = [renderBillboardAd([1, 1, 1]), renderBillboardAd([1, 0, 1])];
  BB_GLINT = renderBillboardGlint(); PITCH = makePitch(BB.w, BB.h, { accent: '#2ec4b6' });
  SKY_C = renderSky(); MOON_C = renderMoon(); MTN_C = renderMountains(); WATER_C = renderWater(); BOAT_C = renderBoat();
  BEACH = renderBeach();
  PALM_C = PALMS.map(p => Array.from({ length: PALM_NF }, (_, f) => renderPalmCrown(p, f / (PALM_NF - 1) * 2 - 1)));
  BAL_C = renderBalcony(); POT_C = renderPot();
  HAM_C = Array.from({ length: HAM_NF }, (_, f) => renderHammock(f / HAM_NF));
  SIT_BC = renderSitter(SIT_BODY, 8); SIT_HC = SIT_HEAD.map(h => renderSitter(h, 0)); SIT_AC = SIT_ARM.map((a, k) => renderSitter(a, SIT_ARM_Y[k]));
  PLANTS = [...FRONDS.map(renderFrond), ...STREL.map(u => renderPlantUnit(u, false)), ...[1, 2, 0, 4, 3].map(i => renderPlantUnit(MONSTERA[i], true))];
  VIG_C = renderVignette();
}

/* ================= Frame compositor (back to front) ================= */
function drawWater(t) {
  ctx.drawImage(WATER_C, 0, HZ);
  reflect(ctx, t, { top: HZ, rows: 48, squash: 1.35, alpha: 0.42, k1: 40, k2: 96, amp: 0.5, grow: 0.045 });
  rect(ctx, 0, HZ, W, 48, 'rgba(8,24,44,0.2)');
  for (const s of WSTREAKS) streak(ctx, t, s.x, s.w, s.col, s.a, s.seed, HZ, HZ + 22);
  drawRipples(t);
  drawMoonPath(t);
  drawBoat(t);
}
// pitch: the "your ad here" face (promo captures) on the billboard; billboard: false leaves the sign out (the loop
// videos); weather: a WEATHERS pick (site/js/engine/weather.js), the city's own when left out
function renderFrame(t, { pitch, billboard = true, weather, reducedMotion = false } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX;
  ctx.drawImage(SKY_C, 0, 0);
  for (const s of STARS) {
    const a = s.b * (0.55 + 0.45 * wave(t, s.k, s.ph));
    if (s.big) { ctx.globalAlpha = a * 0.35; rect(ctx, s.x - 1, s.y, 3, 1, s.col); rect(ctx, s.x, s.y - 1, 1, 3, s.col); }
    ctx.globalAlpha = a; px(ctx, s.x, s.y, s.col);
  }
  ctx.globalAlpha = 1;
  drawShootingStars(t);
  halo(HL.moon, MOON.x, MOON.y); halo(HL.moonCore, MOON.x, MOON.y);
  ctx.drawImage(MOON_C, MOON.x - MOON.r, MOON.y - MOON.r);
  drawPlane(t);
  halo(HL.christ, XC, CORC - 14, 0.9 + 0.1 * wave(t, 6));
  ctx.drawImage(MTN_C, 0, 0);
  halo(HL.christCore, XC, CORC - 16, 0.85 + 0.15 * wave(t, 6));
  drawMountainFx(t);
  if (billboard) drawBillboard(t, pitch);
  drawWater(t);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  drawFireworks(t);
  ctx.drawImage(BEACH.base, 0, 0);
  drawWaves(t);
  drawStroll(t);
  drawFutevolei(t, false);
  drawFlies(0, t);
  ctx.drawImage(BEACH.props, 0, 0);
  drawBeachLights(t);
  drawPalms(t);
  drawFutevolei(t, true);
  drawWalkers(t);
  drawCars(t);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  ctx.drawImage(BAL_C, 0, 0);
  drawFairyLights(t);
  drawFlies(1, t);
  drawHammock(t);
  drawPlants(t);
  halo(HL.sconce, SCONCE.x, SCONCE.y, 0.92 + 0.08 * wave(t, 1700)); halo(HL.sconceCore, SCONCE.x, SCONCE.y, 1);
  drawMoths(t);
  drawFlies(2, t);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'rio',
  name: 'Rio de Janeiro',
  tagline: 'Copacabana summer night',
  timeZone: 'America/Sao_Paulo',
  accent: '#2ec4b6',
  focusX: XC,
  landmark: [XC - 8, XC + 8],                          // Christ the Redeemer's arms
  landmarkTop: 26,                                     // the row of the statue's head, which landscape crops keep
  billboard: BB,
  perch: { dy: -1 },                                   // the What's new pigeon (site/js/ui/pigeon.js): the frame sits a row lower
  ambience: { waves: 0.7, crickets: 0.45, wind: 0.15, crowd: 0.1 },
  events: BURSTS.map(b => ({ t: b.t, type: 'firework' })),
  create(c) {
    ctx = c;
    prerender();
    return { render: renderFrame };
  },
};
